<template>
   <div>
      <!-- Debugger Disabled State -->
      <div v-if="debuggerEnabled === false" class="card shadow-sm border-0 mb-4 text-center py-5" style="border-radius: 12px; background: #fff;">
         <div class="card-body py-5">
            <div class="mb-4">
               <span class="d-inline-flex align-items-center justify-content-center bg-danger-soft text-danger rounded-circle" style="width: 80px; height: 80px;">
                  <i class="fa-solid fa-shield-halved fs-1"></i>
               </span>
            </div>
            <h3 class="fw-bold text-dark mb-2">Debugger Dashboard Disabled</h3>
            <p class="text-muted mx-auto" style="max-width: 500px; font-size: 14px;">
               The diagnostics and system tracing debugger is currently disabled on this environment. To enable these features, please configure your server configuration file (e.g. <code>.vertex_config</code>) with:
            </p>
            <div class="d-inline-block bg-dark text-light p-3 rounded-3 font-monospace mb-4" style="font-size: 13px; border: 1px solid #1e293b;">
               XSURFLOG_DEBUGGER_ENABLED=true
            </div>
            <div>
               <span class="badge bg-light border text-secondary px-3 py-2 small"><i class="fa-solid fa-circle-info me-1"></i> Default behavior defaults to false if unset</span>
            </div>
         </div>
      </div>

      <!-- Debugger Loading State -->
      <div v-else-if="debuggerEnabled === null" class="text-center py-5 bg-white rounded-3 shadow-sm border" style="height: 300px; display: flex; flex-direction: column; align-items: center; justify-content: center; border-radius: 12px;">
         <i class="fa-solid fa-spinner fa-spin fs-2 text-secondary mb-3"></i>
         <div class="text-muted fw-bold small">Loading debugger configuration...</div>
      </div>

      <!-- Main Module Dashboard -->
      <div v-else>
         <!-- Premium Multi-Tab Navigation Pill Selectors -->
         <div class="row mb-4">
            <div class="col-12 text-center text-md-start">
               <div class="custom-tabs-container d-inline-flex p-1 rounded-pill bg-light border shadow-sm">
                  <ul class="nav nav-pills custom-tabs mb-0 border-0">
                     <li class="nav-item">
                        <button type="button" class="nav-link rounded-pill py-2 px-4" :class="{ active: activeTab === 'telemetry' }" @click="changeTab('telemetry')">
                           <i class="fa-solid fa-gauge-high me-2"></i>Status & Simulator
                        </button>
                     </li>
                     <li class="nav-item">
                        <button type="button" class="nav-link rounded-pill py-2 px-4" :class="{ active: activeTab === 'logs' }" @click="changeTab('logs')">
                           <i class="fa-solid fa-list-check me-2"></i>Upload Diagnostics
                        </button>
                     </li>
                     <li class="nav-item">
                        <button type="button" class="nav-link rounded-pill py-2 px-4" :class="{ active: activeTab === 'traces' }" @click="changeTab('traces')">
                           <i class="fa-solid fa-terminal me-2"></i>Live System Traces
                        </button>
                     </li>
                     <li class="nav-item">
                        <button type="button" class="nav-link rounded-pill py-2 px-4" :class="{ active: activeTab === 'schema' }" @click="changeTab('schema')">
                           <i class="fa-solid fa-database me-2"></i>Database Schema Explorer
                        </button>
                     </li>
                     <li class="nav-item">
                        <button type="button" class="nav-link rounded-pill py-2 px-4" :class="{ active: activeTab === 'sandbox' }" @click="changeTab('sandbox')">
                           <i class="fa-solid fa-globe me-2"></i>Browser Simulator Sandbox
                        </button>
                     </li>
                     <li class="nav-item">
                        <button type="button" class="nav-link rounded-pill py-2 px-4" :class="{ active: activeTab === 'audit_generator' }" @click="changeTab('audit_generator')">
                           <i class="fa-solid fa-wand-magic-sparkles me-2"></i>Audit Log Generator
                        </button>
                     </li>
                  </ul>
               </div>
            </div>
         </div>

         <!-- Main Module Card -->
         <div class="card shadow-sm border-0 mb-4" style="border-radius: 12px; background: #fff;">
            <div class="card-body m-2">

               <!-- TAB 1: Status & Simulator -->
               <div v-show="activeTab === 'telemetry'">
                  <!-- Metrics Grid -->
                  <div class="row mb-4">
                     <div class="col-md-3 col-sm-6 mb-3">
                        <div class="diagnostic-card bg-light p-3 rounded-3 border-start border-primary border-4 shadow-sm">
                           <div class="text-muted small uppercase fw-bold mb-1">Total Logged Uploads</div>
                           <div class="d-flex align-items-center">
                              <i class="fa-solid fa-cloud-arrow-up text-primary fs-4 me-2"></i>
                              <span class="fs-2 fw-bold text-dark">{{ totalUploads }}</span>
                           </div>
                        </div>
                     </div>
                     <div class="col-md-3 col-sm-6 mb-3">
                        <div class="diagnostic-card bg-light p-3 rounded-3 border-start border-success border-4 shadow-sm">
                           <div class="text-muted small uppercase fw-bold mb-1">Pipeline Status</div>
                           <div class="d-flex align-items-center">
                              <i class="fa-solid fa-circle-check text-success fs-4 me-2"></i>
                              <span class="fs-4 fw-bold text-success">{{ systemStatus }}</span>
                           </div>
                        </div>
                     </div>
                     <div class="col-md-3 col-sm-6 mb-3">
                        <div class="diagnostic-card bg-light p-3 rounded-3 border-start border-info border-4 shadow-sm">
                           <div class="text-muted small uppercase fw-bold mb-1">Capture Engine</div>
                           <div class="d-flex align-items-center">
                              <i class="fa-solid fa-bullseye text-info fs-4 me-2"></i>
                              <span class="fs-4 fw-bold text-info">Active</span>
                           </div>
                        </div>
                     </div>
                     <div class="col-md-3 col-sm-6 mb-3">
                        <div class="diagnostic-card bg-light p-3 rounded-3 border-start border-warning border-4 shadow-sm">
                           <div class="text-muted small uppercase fw-bold mb-1">Database Sync</div>
                           <div class="d-flex align-items-center">
                              <i class="fa-solid fa-database text-warning fs-4 me-2"></i>
                              <span class="fs-5 fw-bold text-dark">Connected</span>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Split Action Panels: Simulator and Stats -->
                  <div class="row">
                     <!-- Simulator Form -->
                     <div class="col-lg-6 mb-3">
                        <div class="card border border-light shadow-sm h-100" style="border-radius: 10px;">
                           <div class="card-header bg-white py-3 border-0 d-flex align-items-center">
                              <i class="fa-solid fa-circle-play text-primary me-2 fs-5"></i>
                              <h5 class="mb-0 fw-bold text-dark" style="font-size: 16px;">Test Upload Simulator</h5>
                           </div>
                           <div class="card-body pt-1">
                              <p class="text-muted small mb-3">
                                 Simulate the exact Chrome extension logging payload to test capture reliability and size recording.
                              </p>
                              
                              <div class="mb-3">
                                 <label class="filter-label">Associated Extension Key</label>
                                 <select v-model="selectedApiKey" class="form-select form-select-sm select-custom">
                                    <option value="" disabled>-- Select an extension key --</option>
                                    <option v-for="key in apiKeys" :key="key.id" :value="key.api_key">
                                       {{ key.name || 'Unnamed PC' }} ({{ truncateKey(key.api_key) }})
                                    </option>
                                 </select>
                              </div>

                              <div class="row">
                                 <div class="col-sm-6 mb-3">
                                    <label class="filter-label">File Name</label>
                                    <input v-model="simulatedFilename" type="text" class="form-control form-control-sm input-custom" placeholder="e.g. project_proposal.pdf" />
                                 </div>
                                 <div class="col-sm-6 mb-3">
                                    <label class="filter-label">File Size (Bytes)</label>
                                    <input v-model.number="simulatedFilesize" type="number" class="form-control form-control-sm input-custom" placeholder="e.g. 2048000" />
                                 </div>
                              </div>

                              <div class="mb-3">
                                 <label class="filter-label">Destination URL</label>
                                 <input v-model="simulatedUrl" type="text" class="form-control form-control-sm input-custom" placeholder="e.g. https://drive.google.com/upload" />
                              </div>

                              <button type="button" class="btn btn-sm text-light w-100 fw-bold" style="background: #00A0C1; border-radius: 8px; height: 38px;" @click="runSimulation" :disabled="simulating || !selectedApiKey || !simulatedFilename">
                                 <span v-if="simulating">
                                    <i class="fa-solid fa-spinner fa-spin me-1"></i> Running simulation...
                                 </span>
                                 <span v-else>
                                    <i class="fa-solid fa-paper-plane me-1"></i> Trigger Simulated Upload Log
                                 </span>
                              </button>
                           </div>
                        </div>
                     </div>

                     <!-- Diagnostics and Connection Checks -->
                     <div class="col-lg-6 mb-3">
                        <div class="card border border-light shadow-sm h-100" style="border-radius: 10px;">
                           <div class="card-header bg-white py-3 border-0 d-flex align-items-center">
                              <i class="fa-solid fa-wave-square text-success me-2 fs-5"></i>
                              <h5 class="mb-0 fw-bold text-dark" style="font-size: 16px;">System Tracing Status</h5>
                           </div>
                           <div class="card-body pt-1">
                              <ul class="list-group list-group-flush border-0 small">
                                 <li class="list-group-item d-flex justify-content-between align-items-center border-0 px-0">
                                    <span class="text-muted"><i class="fa-solid fa-database text-muted me-2"></i>Database Connection</span>
                                    <span class="badge bg-success-soft text-success">Healthy (GORM MySQL)</span>
                                 </li>
                                 <li class="list-group-item d-flex justify-content-between align-items-center border-0 px-0">
                                    <span class="text-muted"><i class="fa-solid fa-server text-muted me-2"></i>GORM Log Table schema</span>
                                    <span class="badge bg-success-soft text-success">Verified (tbl_uploaded_files)</span>
                                 </li>
                                 <li class="list-group-item d-flex justify-content-between align-items-center border-0 px-0">
                                    <span class="text-muted"><i class="fa-solid fa-envelope text-muted me-2"></i>Upload Limit Alerts</span>
                                    <span class="badge bg-primary-soft text-primary">Configured</span>
                                 </li>
                                 <li class="list-group-item d-flex justify-content-between align-items-center border-0 px-0">
                                    <span class="text-muted"><i class="fa-solid fa-code text-muted me-2"></i>Frontend Serialization API</span>
                                    <span class="badge bg-success-soft text-success">Fixed (SearchLogData JSON Lowercase)</span>
                                 </li>
                              </ul>

                              <div class="alert alert-info mt-3 border-0 p-3" style="border-radius: 8px; background-color: #f0fdfd;">
                                 <div class="d-flex">
                                    <i class="fa-solid fa-circle-info text-info fs-5 me-2 mt-1"></i>
                                    <div>
                                       <strong class="text-dark small">Integration Tip</strong>
                                       <p class="text-muted mb-0 xs-text">
                                          The background script uses the <code>logUploadInterception</code> API to record events.
                                          Both filenames and exact byte sizes are saved and synced down instantly to the history streaming pipeline.
                                       </p>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- TAB 2: Upload Diagnostics -->
               <div v-show="activeTab === 'logs'">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                     <div class="d-flex align-items-center gap-2">
                        <i class="fa-solid fa-list-check text-primary fs-5"></i>
                        <h5 class="mb-0 fw-bold text-dark" style="font-size: 16px;">Recorded Upload Log History</h5>
                        <span class="badge rounded-pill bg-light text-muted border">{{ records.length }} files shown</span>
                     </div>
                     <button type="button" class="btn btn-sm btn-outline-secondary d-flex align-items-center" style="border-radius: 8px; font-size: 13px; height: 34px;" @click="fetchDiagnostics" :disabled="loading">
                        <i class="fa-solid fa-arrows-rotate me-1" :class="{ 'fa-spin': loading }"></i> Refresh logs
                     </button>
                  </div>

                  <!-- Table search filter -->
                  <div class="row mb-3">
                     <div class="col-md-4 mb-2">
                        <input v-model="filterFilename" type="text" class="form-control form-control-sm input-custom" placeholder="🔍 Filter by filename..." />
                     </div>
                     <div class="col-md-4 mb-2">
                        <input v-model="filterAccount" type="text" class="form-control form-control-sm input-custom" placeholder="🔍 Filter by organisation/account..." />
                     </div>
                  </div>

                  <!-- Responsive Table wrapper -->
                  <div class="table-responsive border rounded-3 bg-light p-1">
                     <table class="table align-middle table-hover mainTable mb-0 bg-white">
                        <thead>
                           <tr>
                              <th scope="col" style="width: 80px;">Log ID</th>
                              <th scope="col">File Name</th>
                              <th scope="col">File Size</th>
                              <th scope="col">Account Name (ID)</th>
                              <th scope="col">Date Captured</th>
                              <th scope="col">Action ID</th>
                           </tr>
                        </thead>

                        <tbody v-if="loading && records.length === 0">
                           <tr>
                              <td colspan="6" class="text-center py-4">
                                 <i class="fa-solid fa-spinner fa-spin me-1"></i> Fetching upload records...
                              </td>
                           </tr>
                        </tbody>

                        <tbody v-else-if="filteredRecords.length === 0">
                           <tr>
                              <td colspan="6" class="text-center py-4 text-muted">
                                 No recorded file uploads match the search filters.
                              </td>
                           </tr>
                        </tbody>

                        <tbody v-else>
                           <tr v-for="rec in filteredRecords" :key="rec.id">
                              <td style="font-weight: 600; font-size: 13px;">#{{ rec.id }}</td>
                              <td>
                                 <div class="d-flex align-items-center">
                                    <i class="fa-regular fa-file-lines text-muted me-2 fs-5"></i>
                                    <span class="fw-bold text-slate-700">{{ rec.filename }}</span>
                                 </div>
                              </td>
                              <td>
                                 <span class="badge rounded-pill bg-light border text-dark font-monospace" style="font-size: 12px;">
                                    {{ formatBytes(rec.filesize) }}
                                 </span>
                              </td>
                              <td style="font-size: 13px; color: #475569;">
                                 <strong>{{ rec.account_name || 'System Account' }}</strong> 
                                 <span class="text-muted">(#{{ rec.account_id }})</span>
                              </td>
                              <td style="font-size: 12px; color: #64748b;">
                                 {{ formatDate(rec.date_created) }}
                              </td>
                              <td>
                                 <span class="job-id-badge">{{ rec.url_log_id }}</span>
                              </td>
                           </tr>
                        </tbody>
                     </table>
                  </div>
               </div>

               <!-- TAB 3: Live System Traces -->
               <div v-show="activeTab === 'traces'">
                  <!-- System Tracing Console -->
                  <div class="card border border-dark shadow-sm bg-dark" style="border-radius: 12px; border: 1px solid #1e293b !important;">
                     <div class="card-header bg-dark d-flex align-items-center justify-content-between py-3" style="border-bottom: 1px solid #1e293b;">
                        <div class="d-flex align-items-center">
                           <i class="fa-solid fa-terminal text-success me-2 fs-5"></i>
                           <h5 class="mb-0 fw-bold text-light" style="font-size: 15px; font-family: monospace;">System Tracing Logs (Auto-refreshing)</h5>
                        </div>
                        <div class="d-flex align-items-center gap-2">
                           <button type="button" class="btn btn-xs btn-outline-danger" style="border-radius: 6px; font-size: 11px; padding: 4px 10px;" @click="clearDebuggerLogs">
                              <i class="fa-solid fa-trash-can me-1"></i> Clear Console
                           </button>
                           <button type="button" class="btn btn-xs btn-outline-success" style="border-radius: 6px; font-size: 11px; padding: 4px 10px;" @click="fetchDebuggerLogs" :disabled="loadingLogs">
                              <i class="fa-solid fa-sync me-1" :class="{ 'fa-spin': loadingLogs }"></i> Refresh
                           </button>
                        </div>
                     </div>
                     <div class="card-body p-0">
                        <div class="terminal-body p-3 font-monospace" style="background: #0f172a; height: 380px; overflow-y: auto; font-size: 13px; line-height: 1.6;">
                           <div v-if="debugLogs.length === 0" class="text-muted text-center py-5">
                              <i class="fa-solid fa-circle-nodes d-block mb-2 fs-3 text-secondary"></i>
                              No system traces recorded yet.<br>
                              Run a simulated upload or perform action on the extension to capture live telemetry traces.
                           </div>
                           <div v-else v-for="log in debugLogs" :key="log.id" class="mb-2 d-flex align-items-start log-row">
                              <span class="text-muted me-2 select-none" style="width: 140px; font-size: 11px;">[{{ formatDate(log.date_created) }}]</span>
                              
                              <span :class="getLevelClass(log.level)" class="badge me-2 select-none text-uppercase" style="width: 55px; display: inline-block; text-align: center; font-size: 10px;">
                                 {{ log.level }}
                              </span>
                              
                              <span class="badge bg-secondary-soft text-primary me-2 select-none text-uppercase" style="font-size: 10px; border: 1px solid rgba(0, 160, 193, 0.2);">
                                 {{ log.category }}
                              </span>
                              
                              <span class="text-light-gray flex-grow-1 text-wrap" :class="{ 'text-danger': log.level === 'ERROR', 'text-warning': log.level === 'WARN' }">
                                 {{ log.message }}
                              </span>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- TAB 4: Database Schema Explorer -->
               <div v-show="activeTab === 'schema'">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                     <div class="d-flex align-items-center gap-2">
                        <i class="fa-solid fa-database text-primary fs-5"></i>
                        <h5 class="mb-0 fw-bold text-dark" style="font-size: 16px;">Database Table Schema Explorer</h5>
                     </div>
                     <button type="button" class="btn btn-sm btn-outline-secondary" style="border-radius: 8px; font-size: 13px;" @click="fetchTables" :disabled="loadingSchema">
                        <i class="fa-solid fa-sync me-1" :class="{ 'fa-spin': loadingSchema }"></i> Refresh Tables
                     </button>
                  </div>
                  <p class="text-muted small mb-3">
                     Inspect the physical GORM model schema, column specifications, or extract read-only table logs. Select a table to proceed.
                  </p>

                  <div class="row align-items-end mb-4">
                     <div class="col-md-5 mb-2 mb-md-0">
                        <label class="filter-label">Database Table Name</label>
                        <select v-model="selectedTable" class="form-select form-select-sm select-custom" @change="fetchTableSchema" :disabled="loadingSchema">
                           <option value="" disabled>-- Select a table --</option>
                           <option v-for="table in dbTables" :key="table" :value="table">
                              {{ table }}
                           </option>
                        </select>
                     </div>
                     
                     <div v-if="selectedTable" class="col-md-7 text-md-end">
                        <!-- Sub-tab switch pills -->
                        <div class="btn-group btn-group-sm p-1 bg-light border rounded-3" role="group" style="height: 38px;">
                           <button type="button" class="btn rounded-2 py-1 px-3 border-0 small fw-bold" :class="schemaSubTab === 'columns' ? 'bg-white text-dark shadow-sm' : 'text-muted'" style="font-size: 12px; transition: all 0.2s;" @click="schemaSubTab = 'columns'">
                              <i class="fa-solid fa-table-columns me-1"></i> Schema Columns
                           </button>
                           <button type="button" class="btn rounded-2 py-1 px-3 border-0 small fw-bold" :class="schemaSubTab === 'data' ? 'bg-white text-dark shadow-sm' : 'text-muted'" style="font-size: 12px; transition: all 0.2s;" @click="schemaSubTab = 'data'; fetchTableData()">
                              <i class="fa-solid fa-table me-1"></i> Data Getter (SELECT Runner)
                           </button>
                        </div>
                     </div>
                  </div>

                  <!-- Empty State (No table selected) -->
                  <div v-if="!selectedTable" class="text-muted text-center py-5 bg-light rounded-3 border border-dashed">
                     <i class="fa-solid fa-table-columns fs-3 text-secondary d-block mb-2"></i>
                     Please select a database table from the dropdown above to inspect its properties.
                  </div>

                  <!-- Sub-Tab A: Columns Definition -->
                  <div v-else-if="schemaSubTab === 'columns'">
                     <div v-if="loadingSchema" class="text-center py-5">
                        <i class="fa-solid fa-spinner fa-spin fs-3 text-secondary mb-2"></i>
                        <div>Loading MySQL Table Definitions...</div>
                     </div>
                     
                     <div v-else>
                        <div class="d-flex align-items-center justify-content-between mb-2">
                           <span class="text-success fw-bold font-monospace small"><i class="fa-solid fa-circle-check me-1"></i>Table: {{ selectedTable }}</span>
                           <span class="badge bg-secondary-soft text-primary border font-monospace">{{ tableSchema.length }} columns discovered</span>
                        </div>
                        
                        <div class="table-responsive border rounded-3 bg-light p-1">
                           <table class="table align-middle table-hover mainTable mb-0 bg-white">
                              <thead class="table-light">
                                 <tr>
                                    <th scope="col">Column Field</th>
                                    <th scope="col">Data Type</th>
                                    <th scope="col" style="width: 100px; text-align: center;">Nullable</th>
                                    <th scope="col" style="width: 120px; text-align: center;">Key/Index</th>
                                    <th scope="col">Default Value</th>
                                    <th scope="col">Extra Attributes</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 <tr v-for="col in tableSchema" :key="col.field">
                                    <td>
                                       <strong class="font-monospace text-slate-700" style="font-size: 13px;">{{ col.field }}</strong>
                                    </td>
                                    <td>
                                       <span class="badge rounded-pill bg-light border text-dark font-monospace" style="font-size: 12px; letter-spacing: 0.2px;">
                                          {{ col.type }}
                                       </span>
                                    </td>
                                    <td align="center">
                                       <span v-if="col.null === 'YES'" class="badge bg-success-soft text-success px-2 py-1" style="font-size: 11px;">YES</span>
                                       <span v-else class="badge bg-danger-soft text-danger px-2 py-1" style="font-size: 11px;">NO</span>
                                    </td>
                                    <td align="center">
                                       <span v-if="col.key === 'PRI'" class="badge bg-warning text-dark font-monospace uppercase px-2 py-1" style="font-size: 10px; font-weight: bold;">PRIMARY</span>
                                       <span v-else-if="col.key === 'UNI'" class="badge bg-info text-dark font-monospace uppercase px-2 py-1" style="font-size: 10px;">UNIQUE</span>
                                       <span v-else-if="col.key === 'MUL'" class="badge bg-secondary text-light font-monospace uppercase px-2 py-1" style="font-size: 10px;">INDEX</span>
                                       <span v-else class="text-muted small">—</span>
                                    </td>
                                    <td>
                                       <code v-if="col.default !== null" class="text-danger small font-monospace">{{ col.default }}</code>
                                       <span v-else class="text-muted small">NULL</span>
                                    </td>
                                    <td>
                                       <span v-if="col.extra" class="badge bg-light border text-secondary font-monospace" style="font-size: 11px;">{{ col.extra }}</span>
                                       <span v-else class="text-muted small">—</span>
                                    </td>
                                 </tr>
                              </tbody>
                           </table>
                        </div>
                     </div>
                  </div>

                  <!-- Sub-Tab B: Data Getter SELECT SQL Runner -->
                  <div v-else>
                     <div class="card border-0 bg-light p-3 rounded-3 mb-4">
                        <div class="row">
                           <div class="col-12">
                              <label class="filter-label">Interactive SQL SELECT Query Runner</label>
                              <textarea v-model="customQuery" class="form-control font-monospace p-3 text-light bg-dark" style="font-size: 13px; border-radius: 8px; height: 75px; border: 1px solid #334155; line-height: 1.5;" placeholder="e.g. SELECT * FROM tbl_uploaded_files LIMIT 20"></textarea>
                              
                              <div class="d-flex flex-wrap justify-content-between align-items-center mt-2 gap-2">
                                 <span class="text-muted small d-flex align-items-center" style="font-size: 12px;">
                                    <i class="fa-solid fa-shield-halved text-success me-1 fs-6"></i>
                                    <span>Strictly Read-Only (Permits only SELECT, SHOW, DESCRIBE, and EXPLAIN statements)</span>
                                 </span>
                                 
                                 <div class="d-flex gap-2">
                                    <button type="button" class="btn btn-sm btn-outline-secondary py-1 px-2 text-dark font-monospace" style="font-size: 11px; border-radius: 6px; font-weight: bold; background: #fff;" @click="setSelectAllQuery">
                                       Reset to SELECT * FROM {{ selectedTable }}
                                    </button>
                                    <button type="button" class="btn btn-sm text-light py-1 px-3 fw-bold d-flex align-items-center" style="background: #00A0C1; font-size: 11px; border-radius: 6px; height: 32px;" @click="runCustomQuery" :disabled="loadingData || !customQuery.trim()">
                                       <span v-if="loadingData"><i class="fa-solid fa-spinner fa-spin me-1"></i> Running...</span>
                                       <span v-else><i class="fa-solid fa-play me-1"></i> Execute Query</span>
                                    </button>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Error Block -->
                     <div v-if="queryError" class="alert alert-danger border-0 p-3 mb-3 small d-flex align-items-center" style="border-radius: 8px; background-color: #fef2f2;">
                        <i class="fa-solid fa-triangle-exclamation text-danger fs-5 me-2"></i>
                        <div><strong>SQL Execution Blocked/Failed:</strong> {{ queryError }}</div>
                     </div>

                     <!-- Table rows results loader -->
                     <div v-if="loadingData" class="text-center py-5">
                        <i class="fa-solid fa-spinner fa-spin fs-3 text-secondary mb-2"></i>
                        <div>Fetching data records...</div>
                     </div>

                     <!-- Empty state rows -->
                     <div v-else-if="queryResults.length === 0" class="text-muted text-center py-5 bg-light rounded-3 border border-dashed">
                        <i class="fa-solid fa-folder-open fs-3 text-secondary d-block mb-2"></i>
                        No rows returned from query execution. Adjust your SELECT query parameters.
                     </div>

                     <!-- Data Table Grid -->
                     <div v-else>
                        <div class="d-flex align-items-center justify-content-between mb-2">
                           <span class="text-success fw-bold font-monospace small"><i class="fa-solid fa-circle-check me-1"></i>Results Rendered successfully</span>
                           <span class="badge bg-secondary-soft text-primary border font-monospace">{{ queryResults.length }} records loaded</span>
                        </div>
                        
                        <div class="table-responsive border rounded-3 bg-light p-1" style="max-height: 400px; overflow-y: auto;">
                           <table class="table align-middle table-hover mainTable mb-0 bg-white table-sm">
                              <thead class="table-light sticky-top">
                                 <tr>
                                    <th v-for="col in dataColumns" :key="col" scope="col">{{ col }}</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 <tr v-for="(row, idx) in queryResults" :key="idx">
                                    <td v-for="col in dataColumns" :key="col" class="font-monospace" style="font-size: 12px; max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="row[col] === null ? 'NULL' : String(row[col])">
                                       <span v-if="row[col] === null" class="text-muted italic">NULL</span>
                                       <span v-else-if="typeof row[col] === 'boolean'" class="badge" :class="row[col] ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'">{{ row[col] }}</span>
                                       <span v-else>{{ row[col] }}</span>
                                    </td>
                                 </tr>
                              </tbody>
                           </table>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- TAB 5: Browser Simulator Sandbox -->
               <div v-show="activeTab === 'sandbox'">
                  <!-- Sub-Navigation for Sandbox Tab -->
                  <div class="row mb-3">
                     <div class="col-12 text-center text-md-start">
                        <div class="btn-group rounded-pill overflow-hidden bg-light border p-1 shadow-sm" role="group">
                           <button type="button" class="btn btn-xs rounded-pill px-3 py-1.5" :class="sandboxSubTab === 'simulator' ? 'btn-primary text-white shadow-xs' : 'btn-light text-secondary border-0'" @click="sandboxSubTab = 'simulator'">
                              <i class="fa-solid fa-laptop-code me-1"></i> Interactive Simulator Sandbox
                           </button>
                           <button type="button" class="btn btn-xs rounded-pill px-3 py-1.5" :class="sandboxSubTab === 'catalogue' ? 'btn-primary text-white shadow-xs' : 'btn-light text-secondary border-0'" @click="sandboxSubTab = 'catalogue'">
                              <i class="fa-solid fa-list-ol me-1"></i> Regression URL Catalogue
                           </button>
                        </div>
                     </div>
                  </div>

                  <!-- SUB-TAB A: Interactive Simulator -->
                  <div v-if="sandboxSubTab === 'simulator'">
                     <div class="row">
                        <!-- Col A: Sandbox Policy Configuration Control Panel -->
                        <div class="col-lg-4 mb-4">
                           <div class="p-3 border rounded-3 bg-light h-100 shadow-sm" style="max-height: 520px; overflow-y: auto;">
                              <div class="d-flex align-items-center mb-3">
                                 <i class="fa-solid fa-sliders text-primary fs-5 me-2"></i>
                                 <h5 class="mb-0 fw-bold text-dark" style="font-size: 15px;">Organization Policy Simulator</h5>
                              </div>
                              <p class="text-muted small mb-3">Toggle settings to dynamically test how the browser extension intercepts active sessions and logs block reasons.</p>
                              
                              <!-- Setting Toggles -->
                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-shield-halved text-success me-1"></i>Phishing Protection</div>
                                    <div class="text-muted" style="font-size: 10px;">Blocks password input fields on unsafe pages</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxPhishingEnabled" @change="addSandboxLog('Policy update: Phishing Protection toggled ' + (sandboxPhishingEnabled ? 'ON' : 'OFF'), 'warning')">
                                 </div>
                              </div>

                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-cloud-arrow-up text-primary me-1"></i>DLP (Data Loss Prevention)</div>
                                    <div class="text-muted" style="font-size: 10px;">Blocks uploads on non-whitelisted domains</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxDlpEnabled" @change="addSandboxLog('Policy update: DLP upload blocker toggled ' + (sandboxDlpEnabled ? 'ON' : 'OFF'), 'warning')">
                                 </div>
                              </div>

                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-lock text-danger me-1"></i>Block HTTP Websites</div>
                                    <div class="text-muted" style="font-size: 10px;">Enforces SSL/HTTPS globally across tabs</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxBlockHttp" @change="navigateSandbox()">
                                 </div>
                              </div>

                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-ban text-danger me-1"></i>Block All Uploads</div>
                                    <div class="text-muted" style="font-size: 10px;">Universal restriction on file uploads</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxBlockAllUploads" @change="addSandboxLog('Policy update: Block All Uploads toggled ' + (sandboxBlockAllUploads ? 'ON' : 'OFF'), 'warning')">
                                 </div>
                              </div>

                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-globe text-danger me-1"></i>Block All Websites (357)</div>
                                    <div class="text-muted" style="font-size: 10px;">Blocks any domain unless explicitly whitelisted</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxBlockAllWebsites" @change="navigateSandbox()">
                                 </div>
                              </div>

                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-robot text-primary me-1"></i>Block AI Category (362)</div>
                                    <div class="text-muted" style="font-size: 10px;">Restricts ChatGPT and Claude AI tools</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxAiCategoryBlocked" @change="navigateSandbox()">
                                 </div>
                              </div>

                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-building-flag text-warning me-1"></i>Block Gov TLDs (361)</div>
                                    <div class="text-muted" style="font-size: 10px;">Blocks government .gov and .gov.au TLDs</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxGovTldBlocked" @change="navigateSandbox()">
                                 </div>
                              </div>

                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-users text-primary me-1"></i>Block Social Media (355)</div>
                                    <div class="text-muted" style="font-size: 10px;">Restricts access to Facebook and Instagram</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxCategoryBlocked" @change="navigateSandbox()">
                                 </div>
                              </div>

                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-key text-secondary me-1"></i>Block OAuth Login (360)</div>
                                    <div class="text-muted" style="font-size: 10px;">Blocks external SSO OAuth sign-in paths</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxOauthBlocked" @change="navigateSandbox()">
                                 </div>
                              </div>

                              <div class="mb-2 p-2 bg-white rounded border d-flex align-items-center justify-content-between">
                                 <div>
                                    <div class="fw-bold small text-dark" style="font-size: 12px;"><i class="fa-solid fa-eye-slash text-secondary me-1"></i>Disable Logging</div>
                                    <div class="text-muted" style="font-size: 10px;">Suppresses activity logs for safe events</div>
                                 </div>
                                 <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" v-model="sandboxDisableLogging" @change="addSandboxLog('Policy update: Disable Logging toggled ' + (sandboxDisableLogging ? 'ON' : 'OFF'), 'warning')">
                                 </div>
                              </div>

                              <!-- Quick Navigation Shortcuts -->
                              <div class="mt-3">
                                 <div class="mb-2">
                                    <label class="filter-label" style="font-size: 10px;">Filter Scenarios by Category</label>
                                    <select v-model="selectedScenarioFilter" class="form-select form-select-sm select-custom">
                                       <option value="ALL">-- Show All Scenarios --</option>
                                       <option value="UNSAFE">Unsafe Websites (356)</option>
                                       <option value="DLP">DLP & Upload Blocks</option>
                                       <option value="HTTP">HTTP Connection Blocks (346)</option>
                                       <option value="GOV">Government TLD Blocks (361)</option>
                                       <option value="CATEGORY">Restricted Category Blocks (355)</option>
                                       <option value="DOWNLOAD">Blocked Downloads (350)</option>
                                       <option value="OAUTH">OAuth SSO Blocks (360)</option>
                                    </select>
                                 </div>
                                 
                                 <div class="text-muted small uppercase fw-bold mb-2">Test Scenarios & Target links</div>
                                 <div class="d-flex flex-column gap-1 border rounded-3 p-1 bg-white" style="max-height: 250px; overflow-y: auto;">
                                    <div v-for="sc in filteredScenarios" :key="sc.id" class="d-flex align-items-center justify-content-between p-1 bg-white rounded border">
                                       <div class="d-flex align-items-center flex-grow-1" style="min-width: 0;">
                                          <button type="button" class="btn btn-xs text-start flex-grow-1 border-0 px-2 py-1 text-truncate xs-text" @click="quickNavigate(sc.url)" :title="'Load ' + sc.name + ' in Sandbox'">
                                             <i :class="sc.icon + ' me-1'"></i> <b>{{ sc.id }}.</b> {{ sc.name }}
                                          </button>
                                       </div>
                                       <div class="d-flex align-items-center gap-1 px-1">
                                          <span class="badge bg-light border text-muted px-1 py-0.5 xs-text font-monospace" style="font-size: 8px; line-height: 1;">{{ sc.type }}</span>
                                          <a :href="sc.url" target="_blank" class="btn btn-xs btn-outline-secondary p-1" style="border-radius: 4px; line-height: 1;" :title="'Open ' + sc.name + ' in New Tab'">
                                             <i class="fa-solid fa-arrow-up-right-from-square" style="font-size: 10px;"></i>
                                          </a>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- Col B: Virtual Web Browser Viewport -->
                        <div class="col-lg-8 mb-4">
                           <div class="virtual-browser">
                              <!-- Browser Window Frame Bar -->
                              <div class="browser-header">
                                 <div class="browser-dots">
                                    <div class="browser-dot dot-red"></div>
                                    <div class="browser-dot dot-yellow"></div>
                                    <div class="browser-dot dot-green"></div>
                                 </div>
                                 <div class="d-flex gap-2 ms-2 me-2">
                                    <button type="button" class="btn btn-xs p-1 text-secondary bg-transparent border-0" @click="quickNavigate('https://vertexcybersecurity.com/')"><i class="fa-solid fa-house"></i></button>
                                 </div>
                                 <div class="browser-bar">
                                    <form @submit.prevent="navigateSandbox" class="w-100">
                                       <div class="browser-address">
                                          <i class="fa-solid fa-globe text-secondary" style="font-size: 11px;"></i>
                                          <input type="text" v-model="sandboxUrl" class="border-0 w-100" style="outline: none; font-size: 13px;" placeholder="Type domain and hit Enter..." :disabled="sandboxIsLoading" />
                                          <i v-if="sandboxIsLoading" class="fa-solid fa-spinner fa-spin text-secondary"></i>
                                       </div>
                                    </form>
                                 </div>
                              </div>

                              <!-- Browser Inside Content Screen -->
                              <div class="browser-viewport p-4 position-relative">
                                 
                                 <!-- CASE A: WEBPAGE IS BLOCKED -->
                                 <div v-if="sandboxBlockState" class="h-100 w-100 d-flex flex-column align-items-center justify-content-center text-center py-5">
                                    <div class="mb-3 d-inline-flex p-4 rounded-circle bg-danger-soft text-danger shadow-sm border border-danger border-2 position-relative" style="animation: pulse 2s infinite;">
                                       <i class="fa-solid fa-shield-halved fs-1"></i>
                                       <span class="position-absolute top-50 start-50 translate-middle-x badge rounded-pill bg-danger border border-white font-monospace small" style="font-size: 10px; margin-top: 14px;">{{ sandboxBlockState.code }}</span>
                                    </div>
                                    <h4 class="fw-bold text-dark mb-1">{{ sandboxBlockState.type }} Blocked</h4>
                                    <p class="text-muted font-monospace small mb-3" style="max-width: 480px; font-size: 12px; line-height: 1.5;">{{ sandboxBlockState.message }}</p>
                                    <div class="p-3 bg-white rounded border text-start shadow-xs mb-3" style="max-width: 480px;">
                                       <div class="fw-bold small text-dark mb-1"><i class="fa-solid fa-circle-exclamation text-danger me-1"></i>Diagnosed Error Code</div>
                                       <div class="text-muted font-monospace" style="font-size: 11px;">Entity code: <code>{{ sandboxBlockState.code }}</code> (Blocked by Client organization policy settings)</div>
                                    </div>
                                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="quickNavigate('https://vertexcybersecurity.com/')">
                                       <i class="fa-solid fa-arrow-left me-1"></i> Go Back to Safety
                                    </button>
                                 </div>

                                 <!-- CASE B: WEBPAGE IS ALLOWED -->
                                 <div v-else class="h-100 w-100 d-flex flex-column justify-content-between">
                                    <div>
                                       <div class="d-flex align-items-center justify-content-between pb-2 border-bottom mb-2">
                                          <div class="d-flex align-items-center">
                                             <i class="fa-solid fa-globe text-success fs-5 me-2"></i>
                                             <h5 class="mb-0 fw-bold text-dark" style="font-size: 14px;">{{ sandboxActiveTabUrl }}</h5>
                                             <span class="badge bg-success-soft text-success border ms-2" style="font-size: 10px;"><i class="fa-solid fa-circle-check me-1"></i>Active Sandboxed Proxy</span>
                                          </div>
                                          <span v-if="sandboxUrl.startsWith('http://')" class="badge bg-warning-soft text-warning border" style="font-size: 10px;"><i class="fa-solid fa-unlock me-1"></i>HTTP Only</span>
                                       </div>
                                    </div>

                                    <!-- Live Embedded Proxy Webpage Iframe -->
                                    <div class="flex-grow-1 border rounded bg-white overflow-hidden mb-3" style="min-height: 280px; position: relative;">
                                       <iframe 
                                          :src="getProxyUrl(sandboxActiveTabUrl)" 
                                          class="w-100 h-100 border-0" 
                                          sandbox="allow-scripts allow-same-origin allow-forms"
                                          @load="addSandboxLog('[Iframe] Live proxy viewport successfully loaded target document', 'success')"
                                       ></iframe>
                                    </div>

                                    <!-- Simulated User Actions panel -->
                                    <div class="p-2 bg-white rounded-3 border">
                                       <div class="text-muted small uppercase fw-bold mb-2" style="font-size: 11px;"><i class="fa-solid fa-wand-magic-sparkles text-primary me-1"></i>Simulate User Interactions</div>
                                       <div class="row">
                                          <div class="col-md-4 mb-1">
                                             <button type="button" class="btn btn-sm btn-primary w-100 py-1.5 fw-bold" style="border-radius: 8px; font-size: 12px;" @click="simulateSandboxUpload()">
                                                <i class="fa-solid fa-cloud-arrow-up me-1"></i> File Upload (.pdf)
                                             </button>
                                          </div>
                                          <div class="col-md-4 mb-1">
                                             <button type="button" class="btn btn-sm btn-outline-danger w-100 py-1.5 fw-bold" style="border-radius: 8px; font-size: 12px;" @click="simulateSandboxPassword()">
                                                <i class="fa-solid fa-key me-1"></i> Submit Login Form
                                             </button>
                                          </div>
                                          <div class="col-md-4 mb-1">
                                             <button type="button" class="btn btn-sm btn-outline-warning w-100 py-1.5 fw-bold" style="border-radius: 8px; font-size: 12px;" @click="simulateSandboxDownload()">
                                                <i class="fa-solid fa-download me-1"></i> File Download (.exe)
                                             </button>
                                          </div>
                                       </div>
                                    </div>
                                 </div>

                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- SUB-TAB B: Regression URL Catalogue -->
                  <div v-else-if="sandboxSubTab === 'catalogue'" class="p-3 border rounded-3 bg-light shadow-sm mb-3">
                     <div class="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
                        <div class="d-flex align-items-center">
                           <i class="fa-solid fa-list-ol text-primary fs-4 me-2"></i>
                           <h4 class="mb-0 fw-bold text-dark" style="font-size: 16px;">Regression URL Catalogue</h4>
                        </div>
                        <span class="badge bg-primary-soft text-primary border" style="font-size: 11px;">
                           <i class="fa-solid fa-link me-1"></i> {{ testScenarios.length }} Extracted Test Endpoints
                        </span>
                     </div>
                     <p class="text-muted small mb-4">
                        This catalog compiles all 23 unique target URLs extracted from the XSurfLog Regression Test cases workbook. 
                        Clicking <b>"Open URL in New Tab"</b> will open the website directly in a new browser tab so you can manually test it with your locally active extension.
                     </p>

                     <!-- Searching & Filtering controls -->
                     <div class="row g-3 mb-4">
                        <div class="col-md-6">
                           <label class="filter-label">Search Website or Test Type</label>
                           <div class="input-group">
                              <span class="input-group-text bg-white border-end-0" style="padding-top: 9px;"><i class="fa-solid fa-magnifying-glass text-secondary"></i></span>
                              <input type="text" v-model="catalogueSearchQuery" class="form-control form-control-sm input-custom border-start-0" placeholder="Type query (e.g. BadSSL, OAuth)..." />
                           </div>
                        </div>
                        <div class="col-md-6">
                           <label class="filter-label">Filter by Category</label>
                           <select v-model="catalogueCategoryFilter" class="form-select form-select-sm select-custom">
                              <option value="ALL">-- Show All Scenarios --</option>
                              <option value="UNSAFE">Unsafe Websites (356)</option>
                              <option value="DLP">DLP & Upload Blocks</option>
                              <option value="HTTP">HTTP Connection Blocks (346)</option>
                              <option value="GOV">Government TLD Blocks (361)</option>
                              <option value="CATEGORY">Restricted Category Blocks (355)</option>
                              <option value="DOWNLOAD">Blocked Downloads (350)</option>
                              <option value="OAUTH">OAuth SSO Blocks (360)</option>
                           </select>
                        </div>
                     </div>

                     <!-- Interactive Grid of Cards -->
                     <div class="row" style="max-height: 380px; overflow-y: auto;">
                        <div v-if="filteredCatalogueScenarios.length === 0" class="col-12 py-5 text-center text-muted font-monospace small">
                           No regression URLs matched your search query or filter.
                        </div>
                        <div v-else v-for="sc in filteredCatalogueScenarios" :key="sc.id" class="col-md-6 col-lg-4 mb-3">
                           <div class="card h-100 border shadow-sm" style="border-radius: 10px; background: #fff;">
                              <div class="card-body p-3 d-flex flex-column justify-content-between">
                                 <div>
                                    <div class="d-flex align-items-start justify-content-between mb-2">
                                       <span class="badge bg-light border text-secondary font-monospace" style="font-size: 9px;">ID #{{ sc.id }}</span>
                                       <span class="badge bg-primary-soft text-primary font-monospace" style="font-size: 8px; text-transform: uppercase;">{{ sc.type }}</span>
                                    </div>
                                    <h6 class="fw-bold text-dark mb-1" style="font-size: 13px;"><i :class="sc.icon + ' me-1'"></i> {{ sc.name }}</h6>
                                    <div class="text-muted font-monospace text-truncate mb-3" style="font-size: 11px;" :title="sc.url">{{ sc.url }}</div>
                                 </div>
                                 <div class="d-grid gap-1">
                                    <a :href="sc.url" target="_blank" class="btn btn-sm btn-primary py-1.5 fw-bold xs-text d-flex align-items-center justify-content-center" style="border-radius: 6px; font-size: 11px;">
                                       <i class="fa-solid fa-arrow-up-right-from-square me-1"></i> Open URL in New Tab
                                    </a>
                                    <button type="button" class="btn btn-sm btn-outline-secondary py-1 xs-text" style="border-radius: 6px; font-size: 11px;" @click="sandboxSubTab = 'simulator'; quickNavigate(sc.url)">
                                       <i class="fa-solid fa-laptop-code me-1"></i> Load in Viewport
                                    </button>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Simulated Background Activity Log Terminal -->
                  <div class="row mt-3">
                     <div class="col-12">
                        <div class="card bg-dark border-0 overflow-hidden shadow-sm" style="border-radius: 12px;">
                           <div class="card-header bg-dark border-bottom border-secondary py-2 px-3 d-flex align-items-center justify-content-between">
                              <span class="text-secondary small font-monospace"><i class="fa-solid fa-terminal me-2"></i>Virtual Extension Background Console Log Traces</span>
                              <button type="button" class="btn btn-xs p-1 text-secondary bg-transparent border-0 font-monospace" style="font-size: 11px;" @click="sandboxConsoleLogs = []"><i class="fa-solid fa-trash me-1"></i>Clear Terminal</button>
                           </div>
                           <div class="card-body p-3 bg-dark terminal-body" style="height: 200px; overflow-y: auto;">
                              <div v-if="sandboxConsoleLogs.length === 0" class="text-muted text-center font-monospace small py-5">
                                 Waiting for sandbox connection handshakes or events... Navigate or interact with the browser simulator above.
                              </div>
                              <div v-else v-for="(log, idx) in sandboxConsoleLogs" :key="idx" class="font-monospace small mb-1 xs-text d-flex align-items-start">
                                 <span class="text-secondary me-2">[{{ log.timestamp }}]</span>
                                 <span v-if="log.type === 'error'" class="text-danger me-2">[ERROR]</span>
                                 <span v-else-if="log.type === 'warning'" class="text-warning me-2">[WARN]</span>
                                 <span v-else-if="log.type === 'success'" class="text-success me-2">[SUCCESS]</span>
                                 <span v-else-if="log.type === 'log'" class="text-info me-2">[LOG]</span>
                                 <span v-else-if="log.type === 'mail'" class="text-pink me-2">[MAIL]</span>
                                 <span :class="log.type === 'error' ? 'text-danger' : (log.type === 'warning' ? 'text-warning' : (log.type === 'success' ? 'text-success' : 'text-light-gray'))" style="line-height: 1.4;">{{ log.message }}</span>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- TAB 6: Multi-Tenant Audit Log Load Generator -->
               <div v-show="activeTab === 'audit_generator'">
                  <!-- Vertex Header Banner -->
                  <div class="row mb-4">
                     <div class="col-12">
                        <div class="vertex-header-banner p-3 p-md-4 rounded-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                           <div class="d-flex align-items-center mb-3 mb-md-0">
                              <div class="vertex-icon-circle me-3">
                                 <i class="fa-solid fa-wand-magic-sparkles"></i>
                              </div>
                              <div>
                                 <div class="d-flex align-items-center gap-2">
                                    <h5 class="mb-0 fw-bold vertex-title">Multi-Tenant Audit Log & Load Generator</h5>
                                    <span class="badge vertex-badge-subtle">Engine v2.0</span>
                                 </div>
                                 <p class="text-muted small mb-0 mt-1">
                                    Simulate high-volume audit logs, test automated Allow/Block domain triggers, and benchmark system throughput across multiple tenant sessions.
                                 </p>
                              </div>
                           </div>
                           <div class="d-flex align-items-center gap-2 flex-wrap">
                              <button type="button" class="btn btn-vertex-outline" :disabled="verifyingIsolation || generatingAuditLogs" @click="verifyTenantIsolation(true)">
                                 <i v-if="verifyingIsolation" class="fa-solid fa-spinner fa-spin me-1"></i>
                                 <i v-else class="fa-solid fa-shield-halved me-1" style="color: #00A0C1;"></i>
                                 <span>Verify Data Isolation</span>
                              </button>
                              <button type="button" class="btn btn-vertex-danger" :disabled="purgingAuditLogs || generatingAuditLogs" @click="openPurgeModal('all')">
                                 <i v-if="purgingAuditLogs" class="fa-solid fa-spinner fa-spin me-1"></i>
                                 <i v-else class="fa-solid fa-trash-can me-1"></i>
                                 <span>Purge Simulated Logs</span>
                              </button>
                              <button v-if="generatingAuditLogs" type="button" class="btn btn-vertex-danger shadow-sm d-flex align-items-center gap-2" @click="cancelAuditSimulation">
                                 <i class="fa-solid fa-circle-stop fa-beat text-danger"></i>
                                 <span>Cancel Simulation ({{ simulationElapsedSeconds }}s)</span>
                              </button>
                              <button v-else type="button" class="btn btn-vertex-primary" @click="runAuditSimulation">
                                 <i class="fa-solid fa-play me-1"></i>
                                 <span>Run Load Simulation</span>
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Active Simulation In-Progress Indicator Banner -->
                  <div v-if="generatingAuditLogs" class="row mb-4">
                     <div class="col-12">
                        <div class="vertex-header-banner p-3 p-md-3 d-flex flex-column gap-2" style="border-left: 4px solid #00A0C1 !important;">
                           <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                              <div class="d-flex align-items-center gap-3">
                                 <div class="vertex-icon-circle" style="background: #e7f6f9; color: #00A0C1;">
                                    <i class="fa-solid fa-arrows-rotate fa-spin"></i>
                                 </div>
                                 <div>
                                    <div class="d-flex align-items-center gap-2 flex-wrap">
                                       <span class="badge vertex-badge-blue">RUNNING SIMULATION LOAD</span>
                                       <span class="badge vertex-calc-pill font-monospace">
                                          <i class="fa-regular fa-clock me-1 text-muted"></i> Elapsed: {{ simulationElapsedSeconds }}s
                                          <span v-if="simulationMode === 'duration' && simulationDuration"> / {{ simulationDuration }}s</span>
                                       </span>
                                       <span class="badge vertex-badge-subtle font-monospace">
                                          <i class="fa-solid fa-gauge me-1"></i> {{ globalRps }} req/s
                                       </span>
                                    </div>
                                    <p class="text-muted small mb-0 mt-1">
                                       Actively generating audit logs across {{ auditSetups.length }} tenant scenario(s). All parameter controls are locked until simulation completes.
                                    </p>
                                 </div>
                              </div>
                           </div>
                           <!-- Animated Progress Bar -->
                           <div class="progress mt-2" style="height: 6px; background: #e2e8f0; border-radius: 4px;">
                              <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style="background-color: #00A0C1;" :style="{ width: simulationMode === 'duration' && simulationDuration ? Math.min(100, Math.round((simulationElapsedSeconds / simulationDuration) * 100)) + '%' : '100%' }"></div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Global Simulation Parameters Card -->
                  <div class="row mb-4">
                     <div class="col-12">
                        <div class="card vertex-card shadow-sm" :class="{ 'opacity-75': generatingAuditLogs }">
                           <div class="card-body p-4">
                              <div class="row g-4 align-items-start">
                                 <!-- Execution Mode Switcher -->
                                 <div class="col-lg-5 col-md-12">
                                    <label class="filter-label mb-2 d-flex align-items-center justify-content-between">
                                       <span><i class="fa-solid fa-sliders me-1 text-custom-blue"></i> Simulation Mode</span>
                                       <span class="text-muted small font-monospace">
                                          {{ simulationMode === 'duration' ? 'Continuous Timed Load' : 'Target Volume Count' }}
                                       </span>
                                    </label>
                                    <div class="d-flex gap-2 mb-3">
                                       <button type="button" class="mode-pill flex-fill text-center" :disabled="generatingAuditLogs" :class="{ 'mode-pill-active': simulationMode === 'duration', 'opacity-75 pe-none': generatingAuditLogs }" @click="simulationMode = 'duration'">
                                          <i class="fa-solid fa-stopwatch me-1"></i> Timed Duration (Seconds)
                                       </button>
                                       <button type="button" class="mode-pill flex-fill text-center" :disabled="generatingAuditLogs" :class="{ 'mode-pill-active': simulationMode === 'count', 'opacity-75 pe-none': generatingAuditLogs }" @click="simulationMode = 'count'">
                                          <i class="fa-solid fa-hashtag me-1"></i> Fixed Record Count
                                       </button>
                                    </div>

                                    <!-- Duration Mode Input & Presets -->
                                    <div v-if="simulationMode === 'duration'">
                                       <div class="d-flex align-items-center gap-2 mb-2">
                                          <div class="input-group input-group-sm">
                                             <span class="input-group-text bg-white border-end-0 text-muted"><i class="fa-solid fa-clock"></i></span>
                                             <input type="number" class="form-control form-control-sm border-start-0" min="1" max="600" :disabled="generatingAuditLogs" v-model.number="simulationDuration" placeholder="Duration in seconds" />
                                             <span class="input-group-text bg-light text-muted">seconds</span>
                                          </div>
                                       </div>
                                       <div class="d-flex flex-wrap gap-1 align-items-center">
                                          <span class="text-muted small me-1">Presets:</span>
                                          <button v-for="d in [10, 30, 60, 120, 300]" :key="d" type="button" class="preset-chip" :disabled="generatingAuditLogs" :class="{ 'preset-chip-active': simulationDuration === d, 'opacity-75 pe-none': generatingAuditLogs }" @click="simulationDuration = d">
                                             {{ d }}s
                                          </button>
                                          <span class="ms-auto badge vertex-calc-pill">
                                             Load: ~{{ (simulationDuration || 0) * globalRps }} total events
                                          </span>
                                       </div>
                                    </div>

                                    <!-- Count Mode Input & Presets -->
                                    <div v-else>
                                       <div class="d-flex align-items-center gap-2 mb-2">
                                          <div class="input-group input-group-sm">
                                             <span class="input-group-text bg-white border-end-0 text-muted"><i class="fa-solid fa-list-ol"></i></span>
                                             <input type="number" class="form-control form-control-sm border-start-0" min="1" max="25000" :disabled="generatingAuditLogs" v-model.number="recordsPerSetup" @input="onGlobalRecordsChange" placeholder="Records per setup" />
                                             <span class="input-group-text bg-light text-muted">records / setup</span>
                                          </div>
                                       </div>
                                       <div class="d-flex flex-wrap gap-1 align-items-center">
                                          <span class="text-muted small me-1">Presets:</span>
                                          <button v-for="c in [50, 250, 500, 1000, 2500, 5000]" :key="c" type="button" class="preset-chip" :disabled="generatingAuditLogs" :class="{ 'preset-chip-active': recordsPerSetup === c, 'opacity-75 pe-none': generatingAuditLogs }" @click="setRecordsPerSetup(c)">
                                             {{ c.toLocaleString() }}
                                          </button>
                                          <span class="ms-auto badge vertex-calc-pill">
                                             Total: ~{{ (recordsPerSetup || 0) * auditSetups.length }} records
                                          </span>
                                       </div>
                                    </div>
                                 </div>

                                 <!-- Throughput RPS Rate Limiter -->
                                 <div class="col-lg-4 col-md-6">
                                    <label class="filter-label mb-2 d-flex align-items-center justify-content-between">
                                       <span><i class="fa-solid fa-gauge text-custom-blue me-1"></i> Throughput Rate (RPS)</span>
                                       <span class="badge vertex-badge-blue">{{ globalRps }} req/s</span>
                                    </label>
                                    <div class="d-flex align-items-center gap-3 pt-1">
                                       <input type="range" class="form-range flex-grow-1 vertex-range" min="1" max="100" step="1" :disabled="generatingAuditLogs" v-model.number="globalRps">
                                       <input type="number" class="form-control form-control-sm text-center" style="width: 70px; height: 34px; border-radius: 6px;" min="1" max="100" :disabled="generatingAuditLogs" v-model.number="globalRps" />
                                    </div>
                                    <div class="d-flex justify-content-between text-muted small mt-1 font-monospace" style="font-size: 11px;">
                                       <span>1 req/s (Gentle)</span>
                                       <span>50 req/s (Standard)</span>
                                       <span>100 req/s (Stress)</span>
                                    </div>
                                 </div>

                                 <!-- Tag Prefix & Add Tenant Action -->
                                 <div class="col-lg-3 col-md-6">
                                    <label class="filter-label mb-2 d-flex align-items-center justify-content-between">
                                       <span><i class="fa-solid fa-tag text-custom-blue me-1"></i> Simulated Tag</span>
                                       <span class="badge bg-light text-muted border" style="font-size: 10px; font-weight: 500;"><i class="fa-solid fa-lock me-1"></i>Static</span>
                                    </label>
                                    <input type="text" class="form-control form-control-sm mb-3 font-monospace fw-bold" style="height: 34px; border-radius: 6px; background-color: #f8fafc; color: #435666; cursor: not-allowed;" value="[SIMULATED]" readonly disabled>
                                    
                                    <button type="button" class="btn btn-vertex-outline w-100" :disabled="generatingAuditLogs" @click="addAuditSetup">
                                       <i class="fa-solid fa-plus me-1"></i> Add Tenant Scenario
                                    </button>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Dynamic Tenant Setup Scenarios List -->
                  <div class="row mb-4">
                     <div class="col-12">
                        <div class="d-flex align-items-center justify-content-between mb-3">
                           <div class="d-flex align-items-center gap-2">
                              <h6 class="fw-bold mb-0 text-dark" style="font-size: 15px;">
                                 <i class="fa-solid fa-layer-group text-custom-blue me-2"></i>Configured Tenant Scenarios ({{ auditSetups.length }})
                              </h6>
                              <span class="badge vertex-badge-subtle">Multi-Tenant Grid</span>
                           </div>
                           <span class="text-muted small">Each scenario simulates an independent actor session and target organisation.</span>
                        </div>

                        <div class="d-flex flex-column gap-3">
                           <div v-for="(setup, sIdx) in auditSetups" :key="sIdx" class="card vertex-card shadow-sm tenant-setup-card" :class="{ 'opacity-75': generatingAuditLogs }" :style="{ borderLeft: `4px solid ${getSetupColor(sIdx)}` }">
                              <div class="card-header bg-white py-2 px-3 border-bottom d-flex align-items-center justify-content-between">
                                 <div class="d-flex align-items-center gap-2">
                                    <span class="badge" :style="{ backgroundColor: getSetupColor(sIdx), color: '#fff', fontSize: '11px', padding: '4px 8px' }">Scenario #{{ sIdx + 1 }}</span>
                                    <span class="fw-bold text-dark small">{{ setup.decodedActor ? setup.decodedActor.name : 'Tenant Simulation Setup' }}</span>
                                    <span v-if="setup.decodedActor" class="text-muted small font-monospace">({{ setup.decodedActor.email }})</span>
                                    <span v-if="setup.decodedActor" class="badge" :class="setup.decodedActor.isSuperAdmin ? 'bg-primary' : 'bg-warning text-dark'" style="font-size: 10.5px;">
                                       <i :class="setup.decodedActor.isSuperAdmin ? 'fa-solid fa-crown' : 'fa-solid fa-user-shield'" class="me-1"></i>{{ setup.decodedActor.role }}
                                    </span>
                                 </div>
                                 <button v-if="auditSetups.length > 1" type="button" class="btn btn-vertex-danger btn-vertex-sm" :disabled="generatingAuditLogs" @click="removeAuditSetup(sIdx)">
                                    <i class="fa-solid fa-trash me-1"></i> Remove
                                 </button>
                              </div>

                              <div class="card-body p-3">
                                 <div class="row g-3">
                                    <!-- Token Input -->
                                    <div class="col-md-6">
                                       <div class="d-flex align-items-center justify-content-between mb-1">
                                          <label class="filter-label mb-0">
                                             <i class="fa-solid fa-key me-1 text-custom-blue"></i> Bearer / JWT Token
                                          </label>
                                          <button type="button" class="btn btn-vertex-outline btn-vertex-sm py-0" :disabled="generatingAuditLogs" @click="useActiveSessionToken(sIdx)">
                                             <i class="fa-solid fa-bolt me-1"></i> Use Active Session
                                          </button>
                                       </div>
                                       <textarea class="form-control form-control-sm font-monospace" rows="2" style="border-radius: 8px;" :disabled="generatingAuditLogs" v-model="setup.token" @input="inspectJwt(sIdx)" placeholder="Paste raw JWT token (or click Use Active Session)..."></textarea>
                                       
                                       <!-- Decoded Token Inspector Badge -->
                                       <div class="mt-1">
                                          <div v-if="setup.decodedActor" class="vertex-token-chip d-flex align-items-center justify-content-between flex-wrap gap-1">
                                             <div class="d-flex align-items-center gap-1">
                                                <i class="fa-solid fa-circle-check text-success"></i>
                                                <span><strong>{{ setup.decodedActor.name }}</strong> (ID: #{{ setup.decodedActor.userId }}) &bull; {{ setup.decodedActor.email }}</span>
                                             </div>
                                             <span class="badge" :class="setup.decodedActor.isSuperAdmin ? 'bg-primary' : 'bg-warning text-dark'" style="font-size: 10px;">
                                                <i :class="setup.decodedActor.isSuperAdmin ? 'fa-solid fa-crown' : 'fa-solid fa-user-shield'" class="me-1"></i>{{ setup.decodedActor.role }}
                                             </span>
                                          </div>
                                          <div v-else-if="setup.actorError" class="vertex-token-error d-flex align-items-center gap-2">
                                             <i class="fa-solid fa-triangle-exclamation text-danger"></i>
                                             <span>{{ setup.actorError }}</span>
                                          </div>
                                          <div v-else class="text-muted small font-monospace" style="font-size: 11px;">
                                             <i class="fa-solid fa-info-circle me-1 text-muted"></i> Uses active session actor if left empty.
                                          </div>
                                       </div>
                                    </div>

                                    <!-- Target Organisation -->
                                    <div class="col-md-6">
                                       <div class="d-flex align-items-center justify-content-between mb-1">
                                          <label class="filter-label mb-0">
                                             <i class="fa-solid fa-building me-1 text-custom-blue"></i> Target Organisation
                                          </label>
                                          <span v-if="setup.isOrgLocked" class="badge bg-warning-subtle text-dark border border-warning px-2 py-0" style="font-size: 11px;">
                                             <i class="fa-solid fa-lock me-1 text-warning"></i>Locked to Assigned Org
                                          </span>
                                       </div>
                                       <select class="form-select form-select-sm" style="border-radius: 8px; height: 38px;" :disabled="generatingAuditLogs || setup.isOrgLocked" v-model="setup.orgUuid">
                                          <option v-if="!setup.isOrgLocked" value="">-- Auto-resolve from Token or First Org --</option>
                                          <option v-for="org in organisationsList" :key="org.org_uuid || org.organisation_uuid || org.id" :value="org.org_uuid || org.organisation_uuid">
                                             {{ org.name || org.account_name || 'Organisation' }} (Account #{{ org.id || org.account_id || 'N/A' }})
                                          </option>
                                          <option v-if="setup.orgUuid && !organisationsList.some(o => (o.org_uuid || o.organisation_uuid) === setup.orgUuid)" :value="setup.orgUuid">
                                             {{ getOrgDisplayName(setup.orgUuid) || setup.orgUuid }} (Assigned Org)
                                          </option>
                                       </select>
                                       <div class="mt-1 d-flex align-items-center justify-content-between flex-wrap text-muted small font-monospace" style="font-size: 11px;">
                                          <div>
                                             <i class="fa-solid fa-fingerprint me-1"></i> Target Org UUID: <span class="text-dark fw-bold">{{ setup.orgUuid || 'Auto-resolved' }}</span>
                                          </div>
                                          <div v-if="setup.isOrgLocked" class="text-warning small" style="font-size: 10.5px;">
                                             <i class="fa-solid fa-shield-halved me-1"></i> {{ setup.decodedActor && setup.decodedActor.role ? setup.decodedActor.role : 'Org Admin' }} restricted to own org
                                          </div>
                                       </div>
                                    </div>

                                    <!-- Target Modules Grid -->
                                    <div class="col-12">
                                       <div class="d-flex align-items-center justify-content-between mb-2">
                                          <label class="filter-label mb-0">
                                             <i class="fa-solid fa-cubes me-1 text-custom-blue"></i> Target Modules to Populate
                                          </label>
                                          <button type="button" class="btn btn-link p-0 text-decoration-none small" style="color: #00A0C1; font-size: 12px; font-weight: 600;" :disabled="generatingAuditLogs" @click="toggleAllModules(sIdx)">
                                             {{ setup.selectedModules.length === availableAuditModules.length ? 'Deselect All' : 'Select All (' + availableAuditModules.length + ')' }}
                                          </button>
                                       </div>
                                       
                                       <div class="row g-2">
                                          <div v-for="mod in availableAuditModules" :key="mod.id" class="col-md-4 col-sm-6">
                                             <div class="module-card p-2 rounded d-flex align-items-center gap-2 cursor-pointer" :class="{ 'module-card-active': setup.selectedModules.includes(mod.id), 'pe-none opacity-75': generatingAuditLogs }" @click="!generatingAuditLogs && toggleSingleModule(sIdx, mod.id)">
                                                <input class="form-check-input ms-0 me-1" type="checkbox" :id="'mod_' + sIdx + '_' + mod.id" :value="mod.id" :disabled="generatingAuditLogs" v-model="setup.selectedModules" @click.stop>
                                                <div class="d-flex align-items-center justify-content-between w-100 min-w-0">
                                                   <span class="small fw-semibold text-truncate">{{ mod.name }}</span>
                                                   <span v-if="mod.badge" class="badge vertex-badge-subtle ms-1" style="font-size: 9px; white-space: nowrap;">{{ mod.badge }}</span>
                                                </div>
                                             </div>
                                          </div>
                                       </div>
                                    </div>

                                    <!-- Action Policy & Volume (in Count Mode) -->
                                    <div class="col-md-6">
                                       <label class="filter-label mb-1">
                                          <i class="fa-solid fa-bolt-lightning me-1 text-custom-blue"></i> Action Policy
                                       </label>
                                       <select class="form-select form-select-sm" style="border-radius: 8px; height: 38px;" :disabled="generatingAuditLogs" v-model="setup.actionType">
                                          <option value="MIX">Realistic Mix (CREATE, UPDATE, DELETE)</option>
                                          <option value="CREATE">CREATE Operations Only</option>
                                          <option value="UPDATE">UPDATE Operations Only</option>
                                          <option value="DELETE">DELETE Operations Only</option>
                                       </select>
                                    </div>
                                    <div class="col-md-6" v-if="simulationMode === 'count'">
                                       <label class="filter-label mb-1">
                                          <i class="fa-solid fa-hashtag me-1 text-custom-blue"></i> Records for this Setup
                                       </label>
                                       <input type="number" class="form-control form-control-sm" style="border-radius: 8px; height: 38px;" min="1" max="25000" :disabled="generatingAuditLogs" v-model.number="setup.count" :placeholder="recordsPerSetup ? recordsPerSetup.toString() : '500'">
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Cross-Tenant Data Isolation & Leakage Verification Panel -->
                  <div v-if="isolationResults" class="row mb-4">
                     <div class="col-12">
                        <div class="card vertex-card shadow-sm" :style="{ borderLeft: isolationResults.passed ? '4px solid #10B981' : '4px solid #EF4444' }">
                           <div class="card-header bg-white py-3 px-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                              <div class="d-flex align-items-center gap-2">
                                 <div class="isolation-icon-badge" :class="isolationResults.passed ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                                    <i class="fa-solid" :class="isolationResults.passed ? 'fa-shield-halved' : 'fa-triangle-exclamation'"></i>
                                 </div>
                                 <div>
                                    <div class="d-flex align-items-center gap-2 flex-wrap">
                                       <h6 class="mb-0 fw-bold text-dark" style="font-size: 15px;">Cross-Tenant Data Isolation & Leakage Audit</h6>
                                       <span class="badge" :class="isolationResults.passed ? 'bg-success text-white' : 'bg-danger text-white'" style="font-size: 11px; padding: 4px 8px; border-radius: 6px;">
                                          {{ isolationResults.passed ? '🛡️ 100% ISOLATED (ZERO LEAKS)' : '⚠️ DATA LEAK DETECTED' }}
                                       </span>
                                    </div>
                                    <span class="text-muted small" style="font-size: 11px;">
                                       Exhaustive partition assertion across {{ isolationResults.total_checks }} query boundaries & database tables.
                                    </span>
                                 </div>
                              </div>
                              <div class="d-flex align-items-center gap-2">
                                 <span class="text-muted small font-monospace">Audited: {{ formatSimDate(isolationResults.timestamp) }}</span>
                                 <button type="button" class="btn btn-sm btn-vertex-outline" :disabled="verifyingIsolation" @click="verifyTenantIsolation(true)">
                                    <i class="fa-solid fa-arrows-rotate me-1" :class="{ 'fa-spin': verifyingIsolation }"></i> Re-verify
                                 </button>
                              </div>
                           </div>

                           <div class="card-body p-3">
                              <!-- 4 Key Metrics Cards -->
                              <div class="row g-3 mb-3">
                                 <div class="col-md-3 col-sm-6">
                                    <div class="p-3 rounded bg-light border">
                                       <div class="text-muted small fw-bold text-uppercase" style="font-size: 11px;">Tenant Boundaries Tested</div>
                                       <div class="d-flex align-items-center mt-1">
                                          <i class="fa-solid fa-arrows-split-up-and-left text-primary me-2 fs-5"></i>
                                          <span class="fs-4 fw-bold text-dark">{{ (isolationResults.boundary_matrix || []).length }}</span>
                                          <span class="text-muted small ms-1">pairs</span>
                                       </div>
                                    </div>
                                 </div>
                                 <div class="col-md-3 col-sm-6">
                                    <div class="p-3 rounded bg-light border">
                                       <div class="text-muted small fw-bold text-uppercase" style="font-size: 11px;">Total Assertions</div>
                                       <div class="d-flex align-items-center mt-1">
                                          <i class="fa-solid fa-list-check text-info me-2 fs-5"></i>
                                          <span class="fs-4 fw-bold text-dark">{{ isolationResults.total_checks || 0 }}</span>
                                          <span class="text-muted small ms-1">checks</span>
                                       </div>
                                    </div>
                                 </div>
                                 <div class="col-md-3 col-sm-6">
                                    <div class="p-3 rounded bg-light border">
                                       <div class="text-muted small fw-bold text-uppercase" style="font-size: 11px;">Cross-Tenant Leaks</div>
                                       <div class="d-flex align-items-center mt-1">
                                          <i class="fa-solid fa-shield-virus me-2 fs-5" :class="isolationResults.leaks_detected === 0 ? 'text-success' : 'text-danger'"></i>
                                          <span class="fs-4 fw-bold" :class="isolationResults.leaks_detected === 0 ? 'text-success' : 'text-danger'">{{ isolationResults.leaks_detected || 0 }}</span>
                                          <span class="text-muted small ms-1">rows</span>
                                       </div>
                                    </div>
                                 </div>
                                 <div class="col-md-3 col-sm-6">
                                    <div class="p-3 rounded bg-light border">
                                       <div class="text-muted small fw-bold text-uppercase" style="font-size: 11px;">Isolation Score</div>
                                       <div class="d-flex align-items-center mt-1">
                                          <i class="fa-solid fa-award text-warning me-2 fs-5"></i>
                                          <span class="fs-4 fw-bold text-dark">{{ isolationResults.isolation_score || '100.0%' }}</span>
                                       </div>
                                    </div>
                                 </div>
                              </div>

                              <!-- Breakdown Grid -->
                              <div class="row g-3">
                                 <!-- Core Module Partition Checks -->
                                 <div class="col-lg-6 col-12">
                                    <div class="border rounded-2 p-2 bg-white">
                                       <div class="fw-bold text-dark small mb-2 px-2 d-flex align-items-center justify-content-between">
                                          <span><i class="fa-solid fa-table-columns text-custom-blue me-1"></i> Core Module Partition Scopes</span>
                                          <span class="badge bg-light text-muted border" style="font-size: 10px;">{{ (isolationResults.module_results || []).length }} Tables</span>
                                       </div>
                                       <div class="table-responsive">
                                          <table class="table table-sm align-middle mb-0" style="font-size: 12px;">
                                             <thead class="bg-light text-muted font-monospace" style="font-size: 10px;">
                                                <tr>
                                                   <th>MODULE</th>
                                                   <th>TABLE</th>
                                                   <th class="text-center">ROWS</th>
                                                   <th class="text-center">LEAKS</th>
                                                   <th class="text-end">STATUS</th>
                                                </tr>
                                             </thead>
                                             <tbody>
                                                <tr v-for="m in isolationResults.module_results" :key="m.table">
                                                   <td class="fw-semibold text-dark">{{ m.module }}</td>
                                                   <td class="font-monospace text-muted" style="font-size: 11px;">{{ m.table }}</td>
                                                   <td class="text-center">{{ (m.rows_audited || 0).toLocaleString() }}</td>
                                                   <td class="text-center">
                                                      <span class="badge" :class="m.leaks_detected === 0 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                                                         {{ m.leaks_detected }}
                                                      </span>
                                                   </td>
                                                   <td class="text-end">
                                                      <span class="badge" :class="m.status === 'STRICTLY ISOLATED' ? 'vertex-badge-green' : 'bg-danger text-white'" style="font-size: 10px;">
                                                         <i class="fa-solid fa-circle-check me-1" v-if="m.status === 'STRICTLY ISOLATED'"></i>
                                                         {{ m.status }}
                                                      </span>
                                                   </td>
                                                </tr>
                                             </tbody>
                                          </table>
                                       </div>
                                    </div>
                                 </div>

                                 <!-- Cross-Tenant Boundary Grid -->
                                 <div class="col-lg-6 col-12">
                                    <div class="border rounded-2 p-2 bg-white">
                                       <div class="fw-bold text-dark small mb-2 px-2 d-flex align-items-center justify-content-between">
                                          <span><i class="fa-solid fa-arrows-split-up-and-left text-custom-blue me-1"></i> Cross-Tenant Boundary Matrix</span>
                                          <span class="badge bg-light text-muted border" style="font-size: 10px;">Reciprocal Queries</span>
                                       </div>
                                       <div class="table-responsive" style="max-height: 368px; overflow-y: auto;">
                                          <table class="table table-sm align-middle mb-0" style="font-size: 12px;">
                                             <thead class="bg-light text-muted font-monospace" style="font-size: 10px;">
                                                <tr>
                                                   <th>SOURCE</th>
                                                   <th>TARGET SCOPE</th>
                                                   <th class="text-end">ISOLATION</th>
                                                </tr>
                                             </thead>
                                             <tbody>
                                                <tr v-if="!isolationResults.boundary_matrix || isolationResults.boundary_matrix.length === 0">
                                                   <td colspan="3" class="text-center text-muted py-4">Add multiple tenant scenarios to inspect cross-boundary pairs.</td>
                                                </tr>
                                                <tr v-for="(b, bIdx) in isolationResults.boundary_matrix" :key="bIdx">
                                                   <td class="fw-semibold text-truncate" style="max-width: 170px;" :title="b.source_tenant">
                                                      <i class="fa-solid fa-building text-secondary me-1"></i>{{ b.source_tenant }}
                                                   </td>
                                                   <td class="text-truncate text-muted" style="max-width: 170px;" :title="b.target_tenant">
                                                      <i class="fa-solid fa-arrow-right text-muted me-1" style="font-size: 10px;"></i>{{ b.target_tenant }}
                                                   </td>
                                                   <td class="text-end">
                                                      <span class="badge" :class="b.leaked_rows === 0 ? 'vertex-badge-green' : 'bg-danger text-white'" style="font-size: 10px;">
                                                         {{ b.leaked_rows === 0 ? '0 Leaks (Pass)' : b.leaked_rows + ' Leaks' }}
                                                      </span>
                                                   </td>
                                                </tr>
                                             </tbody>
                                          </table>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Simulation Telemetry & Generated Records Panel -->
                  <div v-if="auditSimulationResults" class="row mt-4">
                     <div class="col-12">
                        <!-- Metrics Diagnostic Cards -->
                        <div class="row mb-4">
                           <div class="col-md-3 col-sm-6 mb-3">
                              <div class="diagnostic-card bg-light p-3 rounded-3 border-start border-4 border-info shadow-sm">
                                 <div class="text-muted small uppercase fw-bold mb-1">Total Generated</div>
                                 <div class="d-flex align-items-center">
                                    <i class="fa-solid fa-circle-check text-custom-blue fs-4 me-2"></i>
                                    <span class="fs-2 fw-bold" style="color: #00A0C1;">{{ (auditSimulationResults.total_generated || 0).toLocaleString() }}</span>
                                 </div>
                              </div>
                           </div>
                           <div class="col-md-3 col-sm-6 mb-3">
                              <div class="diagnostic-card bg-light p-3 rounded-3 border-start border-4 border-success shadow-sm">
                                 <div class="text-muted small uppercase fw-bold mb-1">Achieved Throughput</div>
                                 <div class="d-flex align-items-center">
                                    <i class="fa-solid fa-gauge text-success fs-4 me-2"></i>
                                    <span class="fs-2 fw-bold text-dark">{{ auditSimulationResults.achieved_rps || '0.00' }}</span>
                                    <span class="text-muted small ms-1">RPS</span>
                                 </div>
                              </div>
                           </div>
                           <div class="col-md-3 col-sm-6 mb-3">
                              <div class="diagnostic-card bg-light p-3 rounded-3 border-start border-4 border-primary shadow-sm">
                                 <div class="text-muted small uppercase fw-bold mb-1">Elapsed Time</div>
                                 <div class="d-flex align-items-center">
                                    <i class="fa-solid fa-clock text-primary fs-4 me-2"></i>
                                    <span class="fs-2 fw-bold text-dark">{{ (auditSimulationResults.elapsed_seconds || 0).toFixed(2) }}</span>
                                    <span class="text-muted small ms-1">sec</span>
                                 </div>
                              </div>
                           </div>
                           <div class="col-md-3 col-sm-6 mb-3">
                              <div class="diagnostic-card bg-light p-3 rounded-3 border-start border-4 border-warning shadow-sm">
                                 <div class="text-muted small uppercase fw-bold mb-1">Active Setups</div>
                                 <div class="d-flex align-items-center">
                                    <i class="fa-solid fa-layer-group text-warning fs-4 me-2"></i>
                                    <span class="fs-2 fw-bold text-dark">{{ auditSetups.length }}</span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- Live Generated Feed Table matching AuditLogs.vue style -->
                        <div class="card vertex-card shadow-sm mb-4">
                           <div class="card-header bg-white py-3 px-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                              <div class="d-flex align-items-center gap-2">
                                 <i class="fa-solid fa-list-check text-custom-blue fs-5"></i>
                                 <h6 class="mb-0 fw-bold text-dark" style="font-size: 15px;">Generated Audit Logs Feed (Sample Preview)</h6>
                              </div>
                              <div class="d-flex align-items-center gap-2 flex-wrap">
                                 <span class="badge vertex-badge-blue">Showing {{ (auditSimulationResults.records || []).length }} of {{ (auditSimulationResults.total_generated || 0).toLocaleString() }} Records</span>
                                 <div class="btn-group btn-group-sm ms-1" v-if="auditSimulationResults.records && auditSimulationResults.records.length > 0">
                                    <button type="button" class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" @click="exportSimulatedLogs('csv')" title="Export feed as CSV">
                                       <i class="fa-solid fa-file-csv text-success"></i>
                                       <span style="font-size: 12px; font-weight: 500;">Export CSV</span>
                                    </button>
                                    <button type="button" class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" @click="exportSimulatedLogs('json')" title="Export feed as JSON">
                                       <i class="fa-solid fa-file-code text-primary"></i>
                                       <span style="font-size: 12px; font-weight: 500;">Export JSON</span>
                                    </button>
                                 </div>
                              </div>
                           </div>

                           <div class="table-responsive">
                              <table class="table align-middle mainTable mb-0">
                                 <thead>
                                    <tr>
                                       <th style="width: 140px;">DATE</th>
                                       <th style="width: 160px;">MODULE</th>
                                       <th style="width: 110px;">ACTION</th>
                                       <th style="width: 100px;">STATUS</th>
                                       <th>MESSAGE</th>
                                       <th style="width: 170px;">ORGANISATION</th>
                                       <th style="width: 180px;">PERFORMED BY</th>
                                       <th class="text-center" style="width: 70px;">DETAILS</th>
                                    </tr>
                                 </thead>
                                 <tbody v-if="!auditSimulationResults.records || auditSimulationResults.records.length === 0">
                                    <tr>
                                       <td colspan="8" class="text-center py-4 text-muted">No simulated records generated yet.</td>
                                    </tr>
                                 </tbody>
                                 <tbody v-else>
                                    <template v-for="(rec, index) in auditSimulationResults.records" :key="rec.id || index">
                                       <tr class="clickable-row" @click="toggleSimRow(index)">
                                          <td>
                                             <span class="date-text">{{ formatSimDate(rec.date_created) }}</span>
                                          </td>
                                          <td>
                                             <span class="module-pill" :style="{ background: getSimModuleInfo(rec.module).bg, color: getSimModuleInfo(rec.module).color }">
                                                <i :class="getSimModuleInfo(rec.module).icon" class="me-1"></i>{{ rec.module }}
                                             </span>
                                          </td>
                                          <td>
                                             <span class="log-type-pill" :class="getSimLogTypeClass(rec.log_type)">
                                                <i :class="getSimLogTypeIcon(rec.log_type)" class="me-1"></i>
                                                {{ formatSimLogType(rec.log_type) }}
                                             </span>
                                          </td>
                                          <td>
                                             <span v-if="!isLogSuccess(rec)" class="status-pill status-failed">
                                                <i class="fa-solid fa-circle-xmark me-1"></i>Failed
                                             </span>
                                             <span v-else class="status-pill status-success">
                                                <i class="fa-solid fa-circle-check me-1"></i>Success
                                             </span>
                                          </td>
                                          <td>
                                             <div class="d-inline-flex align-items-center gap-1 flex-wrap">
                                                <span v-if="isSimulated(rec.log_message)" class="simulated-pill">
                                                   <i class="fa-solid fa-flask me-1"></i>Simulated
                                                </span>
                                                <span class="log-message" v-html="formatLogMessage(rec.log_message)"></span>
                                             </div>
                                          </td>
                                          <td>
                                             <span v-if="rec.org_name || getOrgDisplayName(rec.org_uuid)" class="badge bg-light text-dark border px-2 py-1 font-monospace" style="font-size: 11px; max-width: 160px; display: inline-block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="rec.org_uuid">
                                                <i class="fa-solid fa-building me-1 text-primary"></i>{{ rec.org_name || getOrgDisplayName(rec.org_uuid) }}
                                             </span>
                                             <span v-else-if="rec.org_uuid" class="text-muted small font-monospace" :title="rec.org_uuid">
                                                <i class="fa-solid fa-building me-1"></i>{{ formatOrgUuid(rec.org_uuid) }}
                                             </span>
                                             <span v-else class="text-muted small">
                                                <i class="fa-solid fa-building me-1"></i>Default Org
                                             </span>
                                          </td>
                                          <td>
                                             <span v-if="rec.performed_by && Number(rec.performed_by) !== 0 && (rec.performed_by_first_name || rec.performed_by_last_name)" class="performer-name">
                                                <i class="fa-solid fa-user me-1" style="color: #00A0C1;"></i>{{ rec.performed_by_first_name }} {{ rec.performed_by_last_name }}
                                             </span>
                                             <span v-else class="performer-system">
                                                <i class="fa-solid fa-robot me-1"></i>System Generated
                                             </span>
                                          </td>
                                          <td class="text-center">
                                             <button type="button" class="btn btn-sm btn-expand" :class="{ 'btn-expand-active': expandedSimRecordIndex === index }" @click.stop="toggleSimRow(index)">
                                                <i :class="expandedSimRecordIndex === index ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                                             </button>
                                          </td>
                                       </tr>

                                       <!-- Expandable Details Row matching AuditLogs.vue -->
                                       <tr v-if="expandedSimRecordIndex === index" class="details-row">
                                          <td colspan="8">
                                             <div class="details-container">
                                                <div class="data-section">
                                                   <div class="data-label text-danger">
                                                      <i class="fa-solid fa-clock-rotate-left me-1"></i> DATA BEFORE
                                                   </div>
                                                   <pre v-html="highlightJson(rec.data_before)"></pre>
                                                </div>
                                                <div class="data-section">
                                                   <div class="data-label text-success">
                                                      <i class="fa-solid fa-check me-1"></i> DATA AFTER
                                                   </div>
                                                   <pre v-html="highlightJson(rec.data_after)"></pre>
                                                </div>
                                             </div>
                                          </td>
                                       </tr>
                                    </template>
                                 </tbody>
                              </table>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

            </div>
         </div>
      </div>
      <!-- Purge Simulated Logs Confirmation Modal (Vertex UI Design) -->
      <div class="modal-backdrop-custom" v-if="showPurgeModal" @click.self="closePurgeModal()">
         <div class="modal-content-custom vertex-modal-card">
            <button @click="closePurgeModal()" class="close-button-custom" aria-label="Close">&times;</button>
            <div class="d-flex align-items-center gap-3 mb-2">
               <div class="vertex-icon-circle-danger">
                  <i class="fa-solid fa-trash-can"></i>
               </div>
               <div>
                  <h5 class="modal-title-custom text-dark mb-0">Purge Simulated Audit Logs</h5>
                  <small class="text-muted">Permanently clean up synthetic test &amp; benchmark records</small>
               </div>
            </div>
            
            <hr>
            
            <div class="modal-body-custom">
               <div class="alert alert-danger-vertex d-flex align-items-start gap-2 mb-3">
                  <i class="fa-solid fa-triangle-exclamation text-danger fs-5 mt-1"></i>
                  <div class="small">
                     This action will permanently delete all audit log records containing the simulation prefix <strong class="font-monospace text-danger">{{ customAuditPrefix || '[SIMULATED]' }}</strong> from the database.
                  </div>
               </div>

               <div class="vertex-modal-details-card mb-3">
                  <div class="d-flex justify-content-between align-items-center py-2 border-bottom">
                     <span class="text-muted small">Target Scope:</span>
                     <span class="badge vertex-badge-blue">
                        {{ purgeTargetOrg === 'all' ? 'All Organisations / Tenants' : purgeTargetOrg }}
                     </span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center py-2 border-bottom">
                     <span class="text-muted small">Simulation Tag:</span>
                     <span class="simulated-pill">
                        <i class="fa-solid fa-flask me-1"></i>{{ customAuditPrefix || '[SIMULATED]' }}
                     </span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center py-2">
                     <span class="text-muted small">Production Records:</span>
                     <span class="text-success small fw-semibold">
                        <i class="fa-solid fa-shield-halved me-1"></i>Safe &amp; Untouched
                     </span>
                  </div>
               </div>

               <p class="small text-muted mb-0">
                  Are you sure you want to proceed? This operation cannot be undone.
               </p>
            </div>
            
            <div class="modal-footer-custom">
               <button type="button" class="btn btn-vertex-outline" :disabled="purgingAuditLogs" @click="closePurgeModal()">
                  Cancel
               </button>
               <button type="button" class="btn btn-vertex-danger-solid" :disabled="purgingAuditLogs" @click="confirmPurgeSimulatedLogs()">
                  <i v-if="purgingAuditLogs" class="fa-solid fa-spinner fa-spin me-1"></i>
                  <i v-else class="fa-solid fa-trash-can me-1"></i>
                  <span>{{ purgingAuditLogs ? 'Purging Records...' : 'Purge Simulated Logs' }}</span>
               </button>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import axios from 'axios';
import exportFromJSON from 'export-from-json';

export default {
   data() {
      return {
         debuggerEnabled: null, // null = loading, false = disabled, true = enabled
         activeTab: 'telemetry',
         records: [],
         apiKeys: [],
         totalUploads: 0,
         systemStatus: 'Operational',
         loading: false,
         
         // Simulator
         selectedApiKey: '',
         simulatedFilename: 'annual_report_2026.pdf',
         simulatedFilesize: 2451000,
         simulatedUrl: 'https://drive.google.com/drive/my-drive',
         simulating: false,

         // Filters
         filterFilename: '',
         filterAccount: '',

         // Real-time tracing terminal
         debugLogs: [],
         loadingLogs: false,
         logsInterval: null,

         // Database Schema Inspector
         dbTables: [],
         selectedTable: '',
         schemaSubTab: 'columns',
         tableSchema: [],
         loadingSchema: false,

         // Data Getter SELECT Query Runner
         customQuery: '',
         queryResults: [],
         loadingData: false,
         queryError: '',

         // Browser Sandbox
         sandboxUrl: 'http://sneaindia.com/',
         sandboxActiveTabUrl: 'http://sneaindia.com/',
         sandboxIsLoading: false,
         sandboxPhishingEnabled: true,
         sandboxDlpEnabled: true,
         sandboxBlockHttp: true,
         sandboxBlockAllUploads: false,
         sandboxDisableLogging: false,
         sandboxBlockAllWebsites: false,
         sandboxAiCategoryBlocked: false,
         sandboxGovTldBlocked: false,
         sandboxCategoryBlocked: false,
         sandboxOauthBlocked: false,
         sandboxConsoleLogs: [],
         sandboxBlockState: null,
         sandboxSubTab: 'simulator',
         selectedScenarioFilter: 'ALL',
         catalogueSearchQuery: '',
         catalogueCategoryFilter: 'ALL',
         testScenarios: [
            // 1. Unsafe Websites (356)
            { id: 1, name: 'HTTP Sneaindia', category: 'UNSAFE', type: 'Unsafe 356', url: 'http://sneaindia.com/', icon: 'fa-solid fa-triangle-exclamation text-danger' },
            { id: 2, name: 'HTTP BadSSL', category: 'UNSAFE', type: 'Unsafe 356', url: 'http://http.badssl.com/', icon: 'fa-solid fa-shield text-warning' },
            { id: 3, name: 'Babytree Parent', category: 'UNSAFE', type: 'Unsafe 356', url: 'http://babytree.com/', icon: 'fa-solid fa-triangle-exclamation text-danger' },
            { id: 4, name: 'McAfee Testing site', category: 'UNSAFE', type: 'Unsafe 356', url: 'http://testingmcafeesites.com/', icon: 'fa-solid fa-shield text-warning' },
            { id: 5, name: 'Software QA Test', category: 'UNSAFE', type: 'Unsafe 356', url: 'http://softwareqatest.com/', icon: 'fa-solid fa-triangle-exclamation text-danger' },
            
            // 2. DLP & Upload
            { id: 6, name: 'ChatGPT App', category: 'DLP', type: 'AI 362 / DLP', url: 'https://chatgpt.com/', icon: 'fa-solid fa-robot text-primary' },
            { id: 7, name: 'Google Gemini', category: 'DLP', type: 'AI 362 / DLP', url: 'https://gemini.google.com/app', icon: 'fa-solid fa-brain text-primary' },
            { id: 8, name: 'Filebin', category: 'DLP', type: 'Upload Block', url: 'http://filebin.net/', icon: 'fa-solid fa-cloud-arrow-up text-primary' },
            { id: 9, name: 'MediaFire Cloud', category: 'DLP', type: 'Upload Block', url: 'http://app.mediafire.com/', icon: 'fa-solid fa-cloud text-primary' },

            // 3. HTTP Only (346)
            { id: 10, name: 'HTTP Forever', category: 'HTTP', type: 'HTTP Block', url: 'http://httpforever.com/', icon: 'fa-solid fa-unlock text-danger' },
            { id: 11, name: 'HTTP Domain.com', category: 'HTTP', type: 'HTTP Block', url: 'http://domain.com', icon: 'fa-solid fa-unlock text-danger' },

            // 4. Gov TLD (361)
            { id: 12, name: 'USA Gov Portal', category: 'GOV', type: 'Gov TLD 361', url: 'https://www.usa.gov/', icon: 'fa-solid fa-building-flag text-warning' },
            { id: 13, name: 'Australia Gov', category: 'GOV', type: 'Gov TLD 361', url: 'https://www.australia.gov.au/', icon: 'fa-solid fa-building-flag text-warning' },
            { id: 14, name: 'Singapore Gov', category: 'GOV', type: 'Gov TLD 361', url: 'https://www.gov.sg/', icon: 'fa-solid fa-building-flag text-warning' },
            { id: 15, name: 'France Gouvernement', category: 'GOV', type: 'Gov TLD 361', url: 'https://www.gouvernement.fr/', icon: 'fa-solid fa-building-flag text-warning' },

            // 5. Category Block (355)
            { id: 16, name: 'Facebook.com', category: 'CATEGORY', type: 'Category 355', url: 'https://facebook.com/', icon: 'fa-solid fa-users text-primary' },
            { id: 17, name: 'Shopee Shop', category: 'CATEGORY', type: 'Category 355', url: 'https://shopee.ph/', icon: 'fa-solid fa-cart-shopping text-primary' },
            { id: 18, name: 'Netflix PH', category: 'CATEGORY', type: 'Category 355', url: 'https://www.netflix.com/ph-en/', icon: 'fa-solid fa-film text-primary' },
            { id: 19, name: 'Amazon E-Commerce', category: 'CATEGORY', type: 'Category 355', url: 'https://www.amazon.com/', icon: 'fa-solid fa-cart-shopping text-primary' },

            // 6. Blocked Downloads (350)
            { id: 20, name: 'Advanced Installer Trial', category: 'DOWNLOAD', type: 'Download 350', url: 'https://www.advancedinstaller.com/trial.html', icon: 'fa-solid fa-download text-warning' },
            { id: 21, name: 'Blender Previous Versions', category: 'DOWNLOAD', type: 'Download 350', url: 'https://www.blender.org/download/previous-versions/', icon: 'fa-solid fa-download text-warning' },

            // 7. OAuth Blocked (360)
            { id: 22, name: 'Meta OAuth Login', category: 'OAUTH', type: 'OAuth 360', url: 'https://auth.meta.com/', icon: 'fa-solid fa-key text-secondary' },
            { id: 23, name: 'Google OAuth Endpoint', category: 'OAUTH', type: 'OAuth 360', url: 'https://accounts.google.com/o/oauth2/auth', icon: 'fa-solid fa-key text-secondary' }
         ],

         // Multi-Tenant Audit Log Generator
         simulationMode: 'duration',
         simulationDuration: 30,
         recordsPerSetup: 500,
         expandedSimRecordIndex: null,
         auditSetups: [
            {
               token: '',
               orgUuid: '',
               selectedModules: ['Allow List', 'Block List', 'Account Settings', 'Category Settings'],
               actionType: 'MIX',
               count: null,
               decodedActor: null,
               actorError: '',
               isSuperAdmin: true,
               isOrgLocked: false
            }
         ],
         globalRps: 20,
         customAuditPrefix: '[SIMULATED]',
         availableAuditModules: [
            { id: 'Allow List', name: 'Allow List', badge: 'Auto Domains', desc: 'Simulates safe domain allow-listing' },
            { id: 'Block List', name: 'Block List', badge: 'Auto Trackers', desc: 'Simulates threat & tracker domain blocks' },
            { id: 'Account Settings', name: 'Account Settings', desc: 'Toggles security and logging configurations' },
            { id: 'Category Settings', name: 'Category Settings', desc: 'Modifies adult, gambling, and web category filters' },
            { id: 'Extension Key', name: 'Extension Keys', desc: 'Simulates key provisioning and revocations' },
            { id: 'Account Government TLDs', name: 'Government TLDs', desc: 'Updates permitted government top-level domains' },
            { id: 'Account AI Categories', name: 'AI Categories', desc: 'Modifies LLM and AI assistant access policies' },
            { id: 'Blocked File Extensions', name: 'Blocked File Extensions', desc: 'Adds/removes blocked file extension types' },
            // { id: 'DoH Block List', name: 'DoH DNS Protection', desc: 'Simulates DNS-over-HTTPS enforcement settings' }
         ],
         generatingAuditLogs: false,
         simulationElapsedSeconds: 0,
         simulationTimer: null,
         simulationAbortController: null,
         simulationCancelled: false,
         purgingAuditLogs: false,
         showPurgeModal: false,
         purgeTargetOrg: 'all',
         auditSimulationResults: null,
         selectedInspectRecord: null,
         organisationsList: [],
         verifyingIsolation: false,
         isolationResults: null,
         autoVerifyIsolation: true
      };
   },
   computed: {
      filteredRecords() {
         return this.records.filter(r => {
            const filenameMatch = !this.filterFilename || 
               (r.filename && r.filename.toLowerCase().includes(this.filterFilename.toLowerCase()));
            const accountMatch = !this.filterAccount || 
               (r.account_name && r.account_name.toLowerCase().includes(this.filterAccount.toLowerCase()));
            return filenameMatch && accountMatch;
         });
      },
      dataColumns() {
         if (this.queryResults.length === 0) return [];
         return Object.keys(this.queryResults[0]);
      },
      filteredScenarios() {
         if (this.selectedScenarioFilter === 'ALL') return this.testScenarios;
         return this.testScenarios.filter(sc => sc.category === this.selectedScenarioFilter);
      },
      filteredCatalogueScenarios() {
         return this.testScenarios.filter(sc => {
            const matchesSearch = !this.catalogueSearchQuery || 
               sc.name.toLowerCase().includes(this.catalogueSearchQuery.toLowerCase()) || 
               sc.url.toLowerCase().includes(this.catalogueSearchQuery.toLowerCase()) ||
               sc.type.toLowerCase().includes(this.catalogueSearchQuery.toLowerCase());
            const matchesCategory = this.catalogueCategoryFilter === 'ALL' || sc.category === this.catalogueCategoryFilter;
            return matchesSearch && matchesCategory;
         });
      }
   },
   created() {
      const isEnabled = process.env.VUE_APP_XSURFLOG_DEBUGGER_ENABLED === 'true';
      this.debuggerEnabled = isEnabled;
      if (this.auditSetups.length > 0) {
         if (this.$store.state.auth.organisation_uuid) {
            this.auditSetups[0].orgUuid = this.$store.state.auth.organisation_uuid;
         }
         this.inspectJwt(0);
      }
      if (isEnabled) {
         this.fetchDiagnostics();
         this.fetchApiKeys();
         this.fetchDebuggerLogs();
         this.fetchOrganisationsList();
         this.logsInterval = setInterval(this.fetchDebuggerLogs, 3000);
      }
   },
   beforeUnmount() {
      if (this.logsInterval) {
         clearInterval(this.logsInterval);
      }
      if (this.simulationTimer) {
         clearInterval(this.simulationTimer);
         this.simulationTimer = null;
      }
      if (this.simulationAbortController) {
         this.simulationAbortController.abort();
         this.simulationAbortController = null;
      }
   },
   methods: {
      changeTab(tab) {
         this.activeTab = tab;
         if (tab === 'schema' && this.dbTables.length === 0) {
            this.fetchTables();
         }
         if (tab === 'sandbox' && this.sandboxConsoleLogs.length === 0) {
            this.navigateSandbox();
         }
         if (tab === 'audit_generator' && this.organisationsList.length === 0) {
            this.fetchOrganisationsList();
         }
      },
      addSandboxLog(message, type = 'info') {
         const timestamp = new Date().toLocaleTimeString();
         this.sandboxConsoleLogs.unshift({ timestamp, message, type });
         if (this.sandboxConsoleLogs.length > 50) this.sandboxConsoleLogs.pop();
      },
      quickNavigate(url) {
         this.sandboxUrl = url;
         this.navigateSandbox();
      },
      async navigateSandbox() {
         let url = this.sandboxUrl.trim();
         if (!url) return;
         if (!/^https?:\/\//i.test(url)) {
            url = 'http://' + url;
         }
         this.sandboxUrl = url;
         this.sandboxIsLoading = true;
         this.addSandboxLog(`Navigating to ${url}...`, 'info');
         this.sandboxBlockState = null;

         try {
            // Extract the domain
            const domain = url.replace(/^(https?:\/\/)?(www\.)?/i, '').split('/')[0].split('?')[0];
            const isHttp = url.startsWith('http://');
            this.addSandboxLog(`[PolicyEngine] Evaluating domain: "${domain}"`, 'process');

            // 1. Check Unsafe 356 check FIRST (takes precedence over protocol blocks)
            if (domain === 'sneaindia.com' || domain === 'http.badssl.com' || domain === 'babytree.com') {
               this.addSandboxLog(`[PolicyEngine] Blocked: Domain flagged in database as Unsafe (356)`, 'error');
               this.sandboxBlockState = {
                  type: 'Unsafe',
                  code: 356,
                  message: 'Unsafe Website Detected — Blocked due to malicious content or security threats identified on this domain.'
               };
               this.sandboxIsLoading = false;
               return;
            }

            // 2. Check Block All Websites (357)
            if (this.sandboxBlockAllWebsites) {
               // Whitelist drive.google.com and vertexcybersecurity.com
               if (domain !== 'drive.google.com' && domain !== 'vertexcybersecurity.com') {
                  this.addSandboxLog(`[PolicyEngine] Blocked: Block All Websites is enabled (357)`, 'error');
                  this.sandboxBlockState = {
                     type: 'Block All',
                     code: 357,
                     message: 'Blocked by XSurfLog Due to Block All Websites Setting Enabled. Only explicitly whitelisted websites are accessible.'
                  };
                  this.sandboxIsLoading = false;
                  return;
               }
            }

            // 3. Check AI Category Blocked (362)
            if (this.sandboxAiCategoryBlocked && (domain === 'chatgpt.com' || domain === 'claude.ai')) {
               this.addSandboxLog(`[PolicyEngine] Blocked: AI and LLM Services category restricted (362)`, 'error');
               this.sandboxBlockState = {
                  type: 'Category Blocked',
                  code: 362,
                  message: "AI website blocked — Artificial Intelligence and LLM services are restricted under your organisation's policy."
               };
               this.sandboxIsLoading = false;
               return;
            }

            // 4. Check Government TLD Blocked (361)
            if (this.sandboxGovTldBlocked && (domain.endsWith('.gov') || domain.endsWith('.gov.au'))) {
               this.addSandboxLog(`[PolicyEngine] Blocked: Government domain TLD restricted (361)`, 'error');
               this.sandboxBlockState = {
                  type: 'Category Blocked',
                  code: 361,
                  message: "Government domain blocked — Access to government TLDs is restricted by your organisation's security policy."
               };
               this.sandboxIsLoading = false;
               return;
            }

            // 5. Check Category Blocked (Social Media) (355)
            if (this.sandboxCategoryBlocked && (domain === 'facebook.com' || domain === 'instagram.com')) {
               this.addSandboxLog(`[PolicyEngine] Blocked: Restricted Category (Social Media) (355)`, 'error');
               this.sandboxBlockState = {
                  type: 'Category Blocked',
                  code: 355,
                  message: "Category blocked — This website falls under a restricted content category (Social Media) blocked by your organisation."
               };
               this.sandboxIsLoading = false;
               return;
            }

            // 6. Check OAuth Login Blocked (360)
            if (this.sandboxOauthBlocked && url.toLowerCase().includes('oauth')) {
               this.addSandboxLog(`[PolicyEngine] Blocked: OAuth authentication restricted (360)`, 'error');
               this.sandboxBlockState = {
                  type: 'OAuth Blocked',
                  code: 360,
                  message: "OAuth login website blocked — Single sign-on authentication through external OAuth providers is restricted."
               };
               this.sandboxIsLoading = false;
               return;
            }

            // 7. Check HTTP Connection Block (346)
            if (isHttp && this.sandboxBlockHttp) {
               this.addSandboxLog(`[PolicyEngine] Blocked: HTTP connection not allowed (346)`, 'error');
               this.sandboxBlockState = {
                  type: 'HTTP Block',
                  code: 346,
                  message: 'Access to this website via HTTP has been blocked by your organisation. Only HTTPS is allowed.'
               };
               this.sandboxIsLoading = false;
               return;
            }

            if (domain === 'chatgpt.com' && this.sandboxBlockAllUploads) {
               this.addSandboxLog(`[PolicyEngine] Chat GPT active — uploads globally restricted`, 'warning');
            }

            this.addSandboxLog(`[PolicyEngine] Navigation allowed (domain marked safe)`, 'success');
            this.sandboxActiveTabUrl = url;
         } catch (err) {
            console.error(err);
         } finally {
            this.sandboxIsLoading = false;
         }
      },
      simulateSandboxUpload() {
         this.addSandboxLog(`[ContentScript] User attempted file drag-and-drop / select on sandbox page`, 'process');
         
         // Check if blocked
         let blocked = false;
         let reason = '';
         const domain = this.sandboxUrl.replace(/^(https?:\/\/)?(www\.)?/i, '').split('/')[0];

         if (this.sandboxBlockAllUploads) {
            blocked = true;
            reason = 'Block All Uploads enabled';
         } else if (domain.includes('chatgpt.com') && this.sandboxDlpEnabled) {
            blocked = true;
            reason = 'DLP is enabled and chatgpt.com is not in common cloud storage whitelist';
         }

         if (blocked) {
            this.addSandboxLog(`[ContentScript] Upload Blocked: ${reason}`, 'error');
            this.$toast.error('Upload Blocked: Organisational policy prevents this file upload.');
            
            // Log to backend if logging not disabled
            if (!this.sandboxDisableLogging) {
               this.addSandboxLog(`[Logger] Log saved: Blocked Upload of "annual_report.pdf" (2.4MB)`, 'log');
            } else {
               this.addSandboxLog(`[Logger] Logging skipped (Disable Logging is enabled)`, 'warning');
            }
         } else {
            this.addSandboxLog(`[ContentScript] Upload Allowed: File "photo_attachment.png" successfully uploaded`, 'success');
            this.$toast.success('Upload complete.');
         }
      },
      simulateSandboxPassword() {
         this.addSandboxLog(`[ContentScript] Password field focused on tab`, 'process');
         if (this.sandboxPhishingEnabled) {
            this.addSandboxLog(`[PolicyEngine] Phishing Warning Triggered (Phishing Protection ON)`, 'error');
            this.sandboxBlockState = {
               type: 'Phishing',
               code: 315,
               message: 'Phishing protection is enabled in extension settings. Only whitelisted password pages are accessible.'
            };

            // Send email if logging not disabled
            if (!this.sandboxDisableLogging) {
               this.addSandboxLog(`[Mailer] Simulated Alert: Phishing email dispatched to administrator (with BlockReasonField populated correctly)`, 'mail');
            } else {
               this.addSandboxLog(`[Mailer] Email skipped (Disable Logging is enabled)`, 'warning');
            }
         } else {
            this.addSandboxLog(`[ContentScript] Input allowed: Site is marked safe/clean from phishing risk`, 'success');
         }
      },
      simulateSandboxDownload() {
         this.addSandboxLog(`[ContentScript] User attempted file download (.exe) on sandbox page`, 'process');
         this.addSandboxLog(`[PolicyEngine] Download Blocked: Restricted extension .exe (350)`, 'error');
         this.$toast.error('Download Blocked: Organisational policy prevents downloading of .exe files.');
         
         if (!this.sandboxDisableLogging) {
            this.addSandboxLog(`[Logger] Log saved: Blocked Download of "setup_installer.exe" (14.5MB)`, 'log');
         } else {
            this.addSandboxLog(`[Logger] Logging skipped (Disable Logging is enabled)`, 'warning');
         }
      },
      getProxyUrl(targetUrl) {
         return this.$axios.defaults.baseURL + "websites/proxy-viewport?url=" + encodeURIComponent(targetUrl);
      },
      async fetchDiagnostics() {
         this.loading = true;
         try {
            const resp = await this.$axios.get("admin-controls/upload-diagnostics");
            if (resp.data && resp.data.success) {
               this.records = resp.data.records || [];
               this.totalUploads = resp.data.total_uploads || 0;
               this.systemStatus = resp.data.system_status || 'Operational';
            }
         } catch (err) {
            console.error("Failed to fetch upload diagnostics:", err);
            this.$toast.error("Could not fetch diagnostic logs.");
         } finally {
            this.loading = false;
         }
      },
      async fetchApiKeys() {
         try {
            const resp = await this.$axios.get("admin-controls/get-api-keys");
            if (resp.data && resp.data.api_keys) {
               this.apiKeys = resp.data.api_keys;
               if (this.apiKeys.length > 0) {
                  this.selectedApiKey = this.apiKeys[0].api_key;
               }
            }
         } catch (err) {
            console.warn("Failed to load extension keys:", err);
         }
      },
      async runSimulation() {
         this.simulating = true;
         try {
            const payload = {
               api_key: this.selectedApiKey,
               url: this.simulatedUrl,
               filename: this.simulatedFilename,
               filesize: String(this.simulatedFilesize),
               is_block_website: false,
               block_type: null,
               has_password_field: 0,
               referrer: 'No Referrer',
               extension_version: '3.1.2'
            };

            const response = await fetch(`${window.location.origin}/api/extension/v1/logs/log-url`, {
               method: 'POST',
               headers: { 'Content-Type': 'application/json' },
               body: JSON.stringify(payload)
            });

            if (response.ok) {
               this.$toast.success("Simulation triggered successfully!");
               setTimeout(() => {
                  this.fetchDiagnostics();
               }, 1000);
            } else {
               this.$toast.error("Failed to send simulation request.");
            }
         } catch (err) {
            console.error("Simulation failed:", err);
            this.$toast.error("Error running upload simulation.");
         } finally {
            this.simulating = false;
         }
      },
      async fetchDebuggerLogs() {
         this.loadingLogs = true;
         try {
            const resp = await this.$axios.get("admin-controls/debugger-logs");
            if (resp.data && resp.data.success) {
               this.debugLogs = resp.data.logs || [];
            }
         } catch (err) {
            console.error("Failed to fetch debugger logs:", err);
         } finally {
            this.loadingLogs = false;
         }
      },
      async clearDebuggerLogs() {
         if (!confirm("Are you sure you want to clear all debugger logs?")) return;
         try {
            const resp = await this.$axios.post("admin-controls/debugger-logs/clear");
            if (resp.data && resp.data.success) {
               this.$toast.success("Debugger logs cleared.");
               this.debugLogs = [];
            }
         } catch (err) {
            this.$toast.error("Failed to clear debugger logs.");
         }
      },
      async fetchTables() {
         this.loadingSchema = true;
         try {
            const resp = await this.$axios.get("admin-controls/database-tables");
            if (resp.data && resp.data.success) {
               this.dbTables = resp.data.tables || [];
               // Pre-select tbl_uploaded_files if available
               if (this.dbTables.includes("tbl_uploaded_files")) {
                  this.selectedTable = "tbl_uploaded_files";
               } else if (this.dbTables.length > 0) {
                  this.selectedTable = this.dbTables[0];
               }
               
               if (this.selectedTable) {
                  this.fetchTableSchema();
               }
            }
         } catch (err) {
            console.error("Failed to fetch database tables:", err);
            this.$toast.error("Could not load database tables.");
         } finally {
            this.loadingSchema = false;
         }
      },
      async fetchTableSchema() {
         if (!this.selectedTable) return;
         this.loadingSchema = true;
         try {
            const resp = await this.$axios.get(`admin-controls/database-schema?table=${this.selectedTable}`);
            if (resp.data && resp.data.success) {
               this.tableSchema = resp.data.columns || [];
               
               // Update query text area for current active sub-tab
               if (this.schemaSubTab === 'data' || !this.customQuery.trim()) {
                  this.setSelectAllQuery();
                  this.runCustomQuery();
               }
            }
         } catch (err) {
            console.error("Failed to fetch table schema:", err);
            this.$toast.error("Could not retrieve table schema.");
         } finally {
            this.loadingSchema = false;
         }
      },
      // Data Getter Methods
      setSelectAllQuery() {
         if (this.selectedTable) {
            this.customQuery = `SELECT * FROM ${this.selectedTable} LIMIT 20`;
         }
      },
      async fetchTableData() {
         if (!this.selectedTable) return;
         if (!this.customQuery.trim() || !this.customQuery.includes(this.selectedTable)) {
            this.setSelectAllQuery();
         }
         await this.runCustomQuery();
      },
      async runCustomQuery() {
         if (!this.customQuery.trim()) return;
         this.loadingData = true;
         this.queryError = '';
         this.queryResults = [];
         try {
            const resp = await this.$axios.get(`admin-controls/database-data?type=raw&query=${encodeURIComponent(this.customQuery)}`);
            if (resp.data && resp.data.success) {
               this.queryResults = resp.data.results || [];
            } else {
               this.queryError = resp.data.error || "Query failed to execute.";
            }
         } catch (err) {
            console.error("Query runner failed:", err);
            if (err.response && err.response.data && err.response.data.error) {
               this.queryError = err.response.data.error;
            } else {
               this.queryError = "Could not execute custom SQL SELECT statement.";
            }
            this.$toast.error("SQL query failed.");
         } finally {
            this.loadingData = false;
         }
      },
      getLevelClass(level) {
         if (level === 'ERROR') return 'bg-danger text-light';
         if (level === 'WARN') return 'bg-warning text-dark';
         return 'bg-success text-dark';
      },
      formatBytes(bytes) {
         if (!bytes || bytes === "undefined") return "0 Bytes";
         const b = parseInt(bytes, 10);
         if (isNaN(b) || b === 0) return bytes;
         const k = 1024;
         const sizes = ["Bytes", "KB", "MB", "GB"];
         const i = Math.floor(Math.log(b) / Math.log(k));
         return parseFloat((b / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
      },
      formatDate(dateStr) {
         if (!dateStr) return "—";
         try {
            const date = new Date(dateStr);
            return date.toLocaleString();
         } catch {
            return dateStr;
         }
      },
      truncateKey(key) {
         if (!key) return '';
         return key.substring(0, 8) + '...' + key.substring(key.length - 8);
      },
      getSetupColor(index) {
         const colors = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#3b82f6', '#8b5cf6'];
         return colors[index % colors.length];
      },
      setRecordsPerSetup(c) {
         this.recordsPerSetup = c;
         this.auditSetups.forEach(s => {
            s.count = null;
         });
      },
      onGlobalRecordsChange() {
         this.auditSetups.forEach(s => {
            s.count = null;
         });
      },
      isPayloadSuperAdmin(payload) {
         if (!payload) return false;
         const roles = Array.isArray(payload.roles) 
            ? payload.roles 
            : (typeof payload.roles === 'string' ? [payload.roles] : (payload.role ? [payload.role] : []));
         const superRoles = ['super_admin', 'superadmin', 'super-admin', 'Super Admin', 'xsurflog_analyst', 'analyst', 'analyst_admin'];
         return roles.some(r => typeof r === 'string' && superRoles.some(sr => sr.toLowerCase() === r.trim().toLowerCase()));
      },
      extractPayloadOrgUuid(payload) {
         if (!payload) return '';
         if (payload.organisation_uuid) return payload.organisation_uuid;
         if (payload.org_uuid) return payload.org_uuid;
         if (payload.orguuid) return payload.orguuid;
         if (Array.isArray(payload.organisation_role) && payload.organisation_role.length > 0) {
            const org = payload.organisation_role[0];
            return org.organisation_uuid || org.org_uuid || org.orguuid || '';
         }
         return '';
      },
      extractPayloadRoleName(payload) {
         if (!payload) return 'Unknown';
         if (this.isPayloadSuperAdmin(payload)) return 'Super Admin';
         if (Array.isArray(payload.organisation_role) && payload.organisation_role.some(r => r.is_organisation_admin)) {
            return 'Organisation Admin';
         }
         if (Array.isArray(payload.roles) && payload.roles.length > 0) {
            return payload.roles.map(r => r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())).join(', ');
         }
         if (payload.role) return payload.role;
         return 'Org Admin';
      },
      addAuditSetup() {
         const activeOrg = this.$store.state.auth.organisation_uuid || '';
         const defaultOrg = activeOrg || (this.organisationsList.length > 0 
            ? (this.organisationsList[0].organisation_uuid || this.organisationsList[0].org_uuid || '') 
            : '');
         const isSuper = !!this.$store.state.auth.isSuperAdmin;
         this.auditSetups.push({
            token: '',
            orgUuid: defaultOrg,
            selectedModules: ['Allow List', 'Block List', 'Account Settings', 'Category Settings'],
            actionType: 'MIX',
            count: null,
            decodedActor: null,
            actorError: '',
            isSuperAdmin: isSuper,
            isOrgLocked: !isSuper
         });
      },
      removeAuditSetup(index) {
         if (this.auditSetups.length > 1) {
            this.auditSetups.splice(index, 1);
         }
      },
      useActiveSessionToken(index) {
         const token = this.$store.state.auth.accessToken || '';
         const activeOrg = this.$store.state.auth.organisation_uuid || '';
         if (this.auditSetups[index]) {
            this.auditSetups[index].token = token;
            if (activeOrg) {
               this.auditSetups[index].orgUuid = activeOrg;
            }
            this.inspectJwt(index);
         }
      },
      inspectJwt(index) {
         const setup = this.auditSetups[index];
         if (!setup) return;

         if (!setup.token || !setup.token.trim()) {
            setup.decodedActor = null;
            setup.actorError = '';
            // When token is empty, inspect active session auth state
            const authState = this.$store.state.auth || {};
            const isSuper = !!authState.isSuperAdmin;
            setup.isSuperAdmin = isSuper;
            setup.isOrgLocked = !isSuper;
            if (!isSuper && authState.organisation_uuid) {
               setup.orgUuid = authState.organisation_uuid;
            }
            return;
         }

         try {
            let raw = setup.token.trim();
            if (raw.toLowerCase().startsWith('bearer ')) {
               raw = raw.substring(7).trim();
            }
            const parts = raw.split('.');
            if (parts.length < 2) {
               setup.actorError = 'Invalid JWT token format';
               setup.decodedActor = null;
               setup.isOrgLocked = false;
               return;
            }
            const base64Url = parts[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
            const payload = JSON.parse(jsonPayload);
            
            const isSuper = this.isPayloadSuperAdmin(payload);
            const userOrgUuid = this.extractPayloadOrgUuid(payload);
            const roleName = this.extractPayloadRoleName(payload);

            setup.decodedActor = {
               userId: payload.user_id || 'N/A',
               name: `${payload.first_name || ''} ${payload.last_name || ''}`.trim() || payload.email || 'Actor',
               email: payload.email || 'N/A',
               orgUuid: userOrgUuid || 'N/A',
               role: roleName,
               isSuperAdmin: isSuper
            };
            setup.actorError = '';
            setup.isSuperAdmin = isSuper;

            if (!isSuper) {
               // Lock strictly to the user's assigned organisation
               if (userOrgUuid) {
                  setup.orgUuid = userOrgUuid;
               }
               setup.isOrgLocked = true;
            } else {
               setup.isOrgLocked = false;
               if (userOrgUuid && !setup.orgUuid) {
                  setup.orgUuid = userOrgUuid;
               }
            }
         } catch (e) {
            setup.actorError = 'Could not decode JWT payload';
            setup.decodedActor = null;
            setup.isOrgLocked = false;
         }
      },
      toggleAllModules(index) {
         const setup = this.auditSetups[index];
         if (!setup) return;
         if (setup.selectedModules.length === this.availableAuditModules.length) {
            setup.selectedModules = [];
         } else {
            setup.selectedModules = this.availableAuditModules.map(m => m.id);
         }
      },
      toggleSingleModule(sIdx, modId) {
         const setup = this.auditSetups[sIdx];
         if (!setup) return;
         const idx = setup.selectedModules.indexOf(modId);
         if (idx > -1) {
            setup.selectedModules.splice(idx, 1);
         } else {
            setup.selectedModules.push(modId);
         }
      },
      async fetchOrganisationsList() {
         if (this.$store.state.auth.organisations && this.$store.state.auth.organisations.length > 0) {
            this.organisationsList = this.$store.state.auth.organisations;
         }
         try {
            const resp = await this.$axios.get('admin-controls/database-data?table=tbl_accounts&limit=50');
            if (resp.data && resp.data.results && resp.data.results.length > 0) {
               this.organisationsList = resp.data.results.map(r => ({
                  id: r.id,
                  account_id: r.id,
                  name: r.account_name || r.name || 'Account #' + r.id,
                  org_uuid: r.org_uuid || r.uuid,
                  organisation_uuid: r.org_uuid || r.uuid
               }));
            }
         } catch (e) {
            console.warn('Could not fetch organisations from database:', e);
         }
         if (this.auditSetups.length > 0 && !this.auditSetups[0].orgUuid) {
            this.auditSetups[0].orgUuid = this.$store.state.auth.organisation_uuid || (this.organisationsList.length > 0 ? (this.organisationsList[0].org_uuid || this.organisationsList[0].organisation_uuid) : '');
         }
      },
      async runAuditSimulation() {
         if (this.generatingAuditLogs) return;

         if (!this.auditSetups || this.auditSetups.length === 0) {
            this.$toast.error('Please configure at least one audit scenario.');
            return;
         }

         this.generatingAuditLogs = true;
         this.simulationCancelled = false;
         this.simulationElapsedSeconds = 0;
         this.expandedSimRecordIndex = null;

         if (this.simulationTimer) {
            clearInterval(this.simulationTimer);
         }
         this.simulationTimer = setInterval(() => {
            this.simulationElapsedSeconds++;
         }, 1000);

         this.simulationAbortController = new AbortController();

         const isDuration = this.simulationMode === 'duration';
         const durationSeconds = isDuration ? (parseInt(this.simulationDuration, 10) || 30) : 0;
         const globalRps = parseInt(this.globalRps, 10) || 20;

         const baseApiUrl = (process.env.VUE_APP_API_BASE_URL ? process.env.VUE_APP_API_BASE_URL.replace(/\/+$/, '') : '') + '/api/main/v1/auth/';

         // Prepare scenarios
         const scenarios = this.auditSetups.map((s, idx) => {
            const token = s.token && s.token.trim() ? s.token.trim() : (this.$store.state.auth.accessToken || '');
            const targetOrg = s.orgUuid || this.$store.state.auth.organisation_uuid || '';
            const modules = (s.selectedModules && s.selectedModules.length > 0) ? s.selectedModules : ['Allow List', 'Block List', 'Account Settings', 'Category Settings'];
            const actType = s.actionType || 'MIX';
            const targetCount = isDuration ? 0 : ((s.count !== null && s.count !== undefined && s.count !== '' && !isNaN(parseInt(s.count, 10)) && parseInt(s.count, 10) > 0) ? parseInt(s.count, 10) : (parseInt(this.recordsPerSetup, 10) || 500));

            const authHeader = token ? (token.toLowerCase().startsWith('bearer ') ? token : `Bearer ${token}`) : '';
            const clientHeaders = {
               'Accept': 'application/json',
               'Content-Type': 'application/json'
            };
            if (authHeader) {
               clientHeaders['Authorization'] = authHeader;
            }

            const client = axios.create({
               baseURL: baseApiUrl,
               headers: clientHeaders,
               timeout: 30000
            });

            return {
               idx,
               token,
               client,
               orgUuid: targetOrg,
               modules,
               actionType: actType,
               targetCount,
               completedCount: 0
            };
         });

         const startTime = performance.now();
         const endTime = isDuration ? startTime + (durationSeconds * 1000) : 0;
         const totalTargetRecords = isDuration ? Infinity : scenarios.reduce((acc, s) => acc + s.targetCount, 0);

         this.auditSimulationResults = {
            total_generated: 0,
            elapsed_seconds: 0,
            achieved_rps: '0.00',
            records: [],
            message: ''
         };

         let totalDispatched = 0;
         let totalGenerated = 0;
         let iteration = 0;
         const inFlightPromises = [];
         const maxConcurrentInFlight = Math.max(20, Math.min(100, globalRps * 2));

         try {
            while (!this.simulationCancelled && !this.simulationAbortController.signal.aborted) {
               const now = performance.now();

               // Termination check
               if (isDuration) {
                  if (now >= endTime) {
                     break;
                  }
               } else {
                  if (totalDispatched >= totalTargetRecords) {
                     break;
                  }
               }

               // Exact quota calculation based on slider RPS
               const elapsedSec = (now - startTime) / 1000;
               const targetDispatched = Math.floor(elapsedSec * globalRps);

               // Dispatch parallel requests to maintain the exact slider rate
               while (
                  totalDispatched <= targetDispatched &&
                  (isDuration || totalDispatched < totalTargetRecords) &&
                  inFlightPromises.length < maxConcurrentInFlight &&
                  !this.simulationCancelled &&
                  !this.simulationAbortController.signal.aborted
               ) {
                  let scenario = null;
                  if (isDuration) {
                     scenario = scenarios[Math.floor(Math.random() * scenarios.length)];
                  } else {
                     const eligible = scenarios.filter(s => s.completedCount + (s.inFlight || 0) < s.targetCount);
                     if (eligible.length === 0) break;
                     scenario = eligible[Math.floor(Math.random() * eligible.length)];
                  }

                  if (!scenario) break;

                  // Pick randomly among all ticked modules for this scenario
                  const mod = (scenario.modules && scenario.modules.length > 0)
                     ? scenario.modules[Math.floor(Math.random() * scenario.modules.length)]
                     : 'Allow List';
                  const actType = scenario.actionType || 'MIX';

                  const payload = {
                     module: mod,
                     action_type: actType,
                     custom_prefix: this.customAuditPrefix || '[SIMULATED]',
                     org_uuid: scenario.orgUuid,
                     index: iteration
                  };

                  const url = `admin-controls/simulate-single-action?org_uuid=${encodeURIComponent(scenario.orgUuid)}`;

                  if (!scenario.inFlight) scenario.inFlight = 0;
                  scenario.inFlight++;
                  totalDispatched++;
                  iteration++;

                  const p = scenario.client.post(url, payload, {
                     signal: this.simulationAbortController.signal
                  }).then(resp => {
                     if (resp.data && resp.data.success && resp.data.record) {
                        totalGenerated++;
                        scenario.completedCount++;
                        this.auditSimulationResults.total_generated = totalGenerated;

                        if (this.auditSimulationResults.records.length < 100) {
                           this.auditSimulationResults.records.unshift(resp.data.record);
                        } else {
                           this.auditSimulationResults.records.pop();
                           this.auditSimulationResults.records.unshift(resp.data.record);
                        }

                        const currentElapsed = Math.max(0.1, (performance.now() - startTime) / 1000);
                        this.auditSimulationResults.elapsed_seconds = currentElapsed;
                        this.auditSimulationResults.achieved_rps = (totalGenerated / currentElapsed).toFixed(2);
                     }
                  }).catch(err => {
                     if (!this.simulationCancelled && !this.simulationAbortController.signal.aborted) {
                        console.warn('Simulation HTTP request error:', err);
                     }
                  }).finally(() => {
                     if (scenario.inFlight > 0) scenario.inFlight--;
                     const pIdx = inFlightPromises.indexOf(p);
                     if (pIdx !== -1) inFlightPromises.splice(pIdx, 1);
                  });

                  inFlightPromises.push(p);
               }

               // Fast 10ms high-precision pacing tick
               await new Promise(res => setTimeout(res, 10));
            }

            // Await remaining in-flight requests
            if (inFlightPromises.length > 0) {
               await Promise.allSettled(inFlightPromises);
            }

            const elapsedFinal = Math.max(0.1, (performance.now() - startTime) / 1000);
            const actualRps = (totalGenerated / elapsedFinal).toFixed(2);
            this.auditSimulationResults.elapsed_seconds = elapsedFinal;
            this.auditSimulationResults.achieved_rps = actualRps;
            this.auditSimulationResults.message = `Successfully generated ${totalGenerated} simulated audit log request(s) across ${scenarios.length} scenario(s) over the wire in ${elapsedFinal.toFixed(2)}s (${actualRps} RPS).`;

            if (!this.simulationCancelled) {
               this.$toast.success(this.auditSimulationResults.message);
               if (this.autoVerifyIsolation) {
                  this.verifyTenantIsolation(false);
               }
            }
         } catch (err) {
            if (this.simulationCancelled || (err && (err.name === 'CanceledError' || err.code === 'ERR_CANCELED'))) {
               this.$toast.info('Load simulation cancelled.');
            } else {
               const msg = (err.response && err.response.data && err.response.data.error) || err.message || 'Error executing audit simulation.';
               this.$toast.error(msg);
            }
         } finally {
            if (this.simulationTimer) {
               clearInterval(this.simulationTimer);
               this.simulationTimer = null;
            }
            this.generatingAuditLogs = false;
            this.simulationAbortController = null;
         }
      },
      cancelAuditSimulation() {
         this.simulationCancelled = true;
         if (this.simulationAbortController) {
            this.simulationAbortController.abort();
            this.simulationAbortController = null;
         }
         if (this.simulationTimer) {
            clearInterval(this.simulationTimer);
            this.simulationTimer = null;
         }
         this.generatingAuditLogs = false;
         this.$toast.info('Load simulation cancelled.');
      },
      async verifyTenantIsolation(showToast = true) {
         this.verifyingIsolation = true;
         try {
            const payload = {
               setups: this.auditSetups.map(s => ({
                  token: s.token,
                  org_uuid: s.orgUuid || this.$store.state.auth.organisation_uuid || '',
                  modules: s.selectedModules,
                  action_types: [s.actionType],
                  count: 0
               }))
            };
            const resp = await this.$axios.post('admin-controls/verify-tenant-isolation', payload);
            if (resp.data && resp.data.success) {
               this.isolationResults = resp.data;
               if (showToast) {
                  if (resp.data.passed) {
                     this.$toast.success(`100% Data Isolation Verified! 0 cross-tenant leaks across ${resp.data.total_checks} checks.`);
                  } else {
                     this.$toast.error(`Warning: ${resp.data.leaks_detected} potential cross-tenant leak(s) detected!`);
                  }
               }
            } else {
               if (showToast) {
                  this.$toast.error((resp.data && resp.data.error) || 'Failed to verify tenant isolation.');
               }
            }
         } catch (err) {
            if (showToast) {
               const msg = (err.response && err.response.data && err.response.data.error) || err.message || 'Error verifying tenant isolation.';
               this.$toast.error(msg);
            }
         } finally {
            this.verifyingIsolation = false;
         }
      },
      openPurgeModal(orgUuid = 'all') {
         this.purgeTargetOrg = orgUuid;
         this.showPurgeModal = true;
      },
      closePurgeModal() {
         if (this.purgingAuditLogs) return;
         this.showPurgeModal = false;
      },
      purgeSimulatedLogs(orgUuid) {
         this.openPurgeModal(orgUuid || 'all');
      },
      async confirmPurgeSimulatedLogs() {
         const prefix = this.customAuditPrefix || '[SIMULATED]';
         this.purgingAuditLogs = true;
         try {
            const resp = await this.$axios.post('admin-controls/purge-simulated-audit-logs', {
               org_uuid: this.purgeTargetOrg || 'all',
               prefix: prefix
            });
            if (resp.data && resp.data.success) {
               this.$toast.success(resp.data.message || 'Simulated logs purged successfully.');
               if (this.auditSimulationResults) {
                  this.auditSimulationResults.records = [];
                  this.auditSimulationResults.total_generated = 0;
               }
               this.showPurgeModal = false;
            } else {
               this.$toast.error((resp.data && resp.data.error) || 'Failed to purge simulated logs.');
            }
         } catch (err) {
            const msg = (err.response && err.response.data && err.response.data.error) || err.message || 'Error purging simulated logs.';
            this.$toast.error(msg);
         } finally {
            this.purgingAuditLogs = false;
         }
      },
      toggleSimRow(index) {
         this.expandedSimRecordIndex = this.expandedSimRecordIndex === index ? null : index;
      },
      exportSimulatedLogs(format = 'csv') {
         if (!this.auditSimulationResults || !this.auditSimulationResults.records || this.auditSimulationResults.records.length === 0) {
            this.$toast.warning('No simulated records available to export.');
            return;
         }

         const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '_');
         const fileName = `simulated_audit_logs_${timestamp}`;

         try {
            if (format === 'json') {
               const exportType = exportFromJSON.types.json;
               const data = this.auditSimulationResults.records.map(rec => {
                  let parsedBefore = null;
                  let parsedAfter = null;
                  try { if (rec.data_before) parsedBefore = typeof rec.data_before === 'string' ? JSON.parse(rec.data_before) : rec.data_before; } catch (e) { parsedBefore = rec.data_before; }
                  try { if (rec.data_after) parsedAfter = typeof rec.data_after === 'string' ? JSON.parse(rec.data_after) : rec.data_after; } catch (e) { parsedAfter = rec.data_after; }

                  return {
                     id: rec.id,
                     uuid: rec.uuid,
                     date_created: rec.date_created,
                     formatted_date: this.formatSimDate(rec.date_created),
                     module: rec.module,
                     action_type: this.formatSimLogType(rec.log_type),
                     status: rec.is_success ? 'Success' : 'Failed',
                     message: rec.log_message,
                     organisation_name: rec.org_name || this.getOrgDisplayName(rec.org_uuid) || 'Default Org',
                     org_uuid: rec.org_uuid || '',
                     account_id: rec.account_id,
                     performed_by: (rec.performed_by && Number(rec.performed_by) !== 0 && `${rec.performed_by_first_name || ''} ${rec.performed_by_last_name || ''}`.trim()) ? `${rec.performed_by_first_name || ''} ${rec.performed_by_last_name || ''}`.trim() : 'System Generated',
                     data_before: parsedBefore,
                     data_after: parsedAfter
                  };
               });
               exportFromJSON({ data, fileName, exportType });
               this.$toast.success(`Successfully exported ${data.length} records as JSON!`);
            } else {
               const exportType = exportFromJSON.types.csv;
               const data = this.auditSimulationResults.records.map(rec => ({
                  'ID': rec.id || '',
                  'Date': this.formatSimDate(rec.date_created),
                  'Module': rec.module || '',
                  'Action': this.formatSimLogType(rec.log_type),
                  'Status': rec.is_success ? 'Success' : 'Failed',
                  'Message': this.cleanLogMessage(rec.log_message || ''),
                  'Organisation': rec.org_name || this.getOrgDisplayName(rec.org_uuid) || 'Default Org',
                  'Org UUID': rec.org_uuid || '',
                  'Performed By': (rec.performed_by && Number(rec.performed_by) !== 0 && `${rec.performed_by_first_name || ''} ${rec.performed_by_last_name || ''}`.trim()) ? `${rec.performed_by_first_name || ''} ${rec.performed_by_last_name || ''}`.trim() : 'System Generated',
                  'Data Before': typeof rec.data_before === 'string' ? rec.data_before : JSON.stringify(rec.data_before || ''),
                  'Data After': typeof rec.data_after === 'string' ? rec.data_after : JSON.stringify(rec.data_after || '')
               }));
               exportFromJSON({ data, fileName, exportType });
               this.$toast.success(`Successfully exported ${data.length} records as CSV!`);
            }
         } catch (err) {
            console.error('Export error:', err);
            this.$toast.error('Failed to export data: ' + err.message);
         }
      },
      getOrgDisplayName(orgUuid) {
         if (!orgUuid) return '';
         if (this.organisationsList && this.organisationsList.length > 0) {
            const found = this.organisationsList.find(o => 
               (o.org_uuid && o.org_uuid === orgUuid) || 
               (o.organisation_uuid && o.organisation_uuid === orgUuid) ||
               (o.uuid && o.uuid === orgUuid)
            );
            if (found && (found.name || found.account_name)) {
               return found.name || found.account_name;
            }
         }
         return '';
      },
      formatOrgUuid(orgUuid) {
         if (!orgUuid) return 'Default Org';
         if (orgUuid.length > 12) {
            return orgUuid.substring(0, 8) + '...';
         }
         return orgUuid;
      },
      formatSimDate(dateVal) {
         if (!dateVal) return 'Just now';
         const d = new Date(dateVal);
         return d.toLocaleString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
         });
      },
      getSimModuleInfo(module) {
         const map = {
            'Allow List': { icon: 'fa-solid fa-circle-check', bg: '#e6f9ee', color: '#00873c' },
            'Block List': { icon: 'fa-solid fa-ban', bg: '#fde8ea', color: '#c62828' },
            'Reported Website': { icon: 'fa-solid fa-flag', bg: '#fff3e0', color: '#e65100' },
            'Category Settings': { icon: 'fa-solid fa-layer-group', bg: '#ede7f6', color: '#6a1b9a' },
            'Account Settings': { icon: 'fa-solid fa-gear', bg: '#e3f2fd', color: '#1565c0' },
            'General Settings': { icon: 'fa-solid fa-gear', bg: '#f1f3f5', color: '#455a64' },
            'API Key': { icon: 'fa-solid fa-key', bg: '#fff8e1', color: '#e6a100' },
            'Extension Key': { icon: 'fa-solid fa-puzzle-piece', bg: '#e7f6f9', color: '#00809a' },
            'Account Domain': { icon: 'fa-solid fa-globe', bg: '#e3f2fd', color: '#1565c0' },
            'Account IP': { icon: 'fa-solid fa-network-wired', bg: '#fce4ec', color: '#c62828' },
            'Account Government TLDs': { icon: 'fa-solid fa-landmark', bg: '#e8eaf6', color: '#283593' },
            'Account AI Categories': { icon: 'fa-solid fa-robot', bg: '#f3e5f5', color: '#7b1fa2' },
            'Blocked File Extensions': { icon: 'fa-solid fa-file-excel', bg: '#fde8ea', color: '#c62828' },
            'DoH Block List': { icon: 'fa-solid fa-shield-virus', bg: '#fff3cd', color: '#856404' }
         };
         return map[module] || { icon: 'fa-solid fa-cube', bg: '#f1f3f5', color: '#6c757d' };
      },
      getSimLogTypeClass(logType) {
         switch (parseInt(logType, 10)) {
            case 1: return 'type-create';
            case 2: return 'type-update';
            case 3: return 'type-delete';
            case 4: return 'type-auth';
            default: return 'type-update';
         }
      },
      getSimLogTypeIcon(logType) {
         switch (parseInt(logType, 10)) {
            case 1: return 'fa-solid fa-plus';
            case 2: return 'fa-solid fa-pen';
            case 3: return 'fa-solid fa-trash';
            case 4: return 'fa-solid fa-key';
            default: return 'fa-solid fa-circle-dot';
         }
      },
      formatSimLogType(logType) {
         switch (parseInt(logType, 10)) {
            case 1: return 'CREATE';
            case 2: return 'UPDATE';
            case 3: return 'DELETE';
            case 4: return 'AUTH';
            default: return 'ACTION';
         }
      },
      highlightJson(dataString) {
         if (!dataString) return '<span class="json-null">null</span>';
         try {
            const parsed = typeof dataString === 'string' ? JSON.parse(dataString) : dataString;
            const jsonStr = JSON.stringify(parsed, null, 2)
               .replace(/&/g, '&amp;')
               .replace(/</g, '&lt;')
               .replace(/>/g, '&gt;');
            return jsonStr.replace(/"(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?/g, (match) => {
               let cls = 'json-string';
               if (/:$/.test(match)) cls = 'json-key';
               return '<span class="' + cls + '">' + match + '</span>';
            })
               .replace(/\b(true|false)\b/g, '<span class="json-boolean">$1</span>')
               .replace(/\b(null)\b/g, '<span class="json-null">$1</span>')
               .replace(/(?<!")\b(-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?)\b(?!")/g, '<span class="json-number">$1</span>');
         } catch {
            return String(dataString).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
         }
      },
      getLogTypeBadge(logType) {
         switch (parseInt(logType, 10)) {
            case 1: return 'bg-success text-white';
            case 2: return 'bg-primary text-white';
            case 3: return 'bg-danger text-white';
            case 4: return 'bg-warning text-dark';
            default: return 'bg-secondary text-white';
         }
      },
      getLogTypeLabel(logType) {
         switch (parseInt(logType, 10)) {
            case 1: return 'CREATE';
            case 2: return 'UPDATE';
            case 3: return 'DELETE';
            case 4: return 'AUTH';
            default: return 'ACTION';
         }
      },
      formatJsonString(jsonStr) {
         if (!jsonStr) return 'null';
         try {
            const obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
            return JSON.stringify(obj, null, 2);
         } catch {
            return jsonStr;
         }
      },
      isSimulated(msg) {
         return !!(msg && msg.includes('[SIMULATED]'));
      },
      cleanLogMessage(msg) {
         if (!msg) return '';
         return msg.replace(/\[SIMULATED\]\s*/g, '');
      },
      formatLogMessage(msg) {
         if (!msg) return '';
         let clean = this.cleanLogMessage(msg);
         clean = clean.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
         return clean.replace(/(?:→|➔|➜|➞|-&gt;)/g, '<span class="transition-arrow"><i class="fa-solid fa-arrow-right"></i></span>');
      },
      isLogSuccess(log) {
         if (!log) return true;
         if (log.is_success === false || log.is_success === 0 || log.is_success === '0') {
            return false;
         }
         if (log.log_message && typeof log.log_message === 'string' && log.log_message.toLowerCase().startsWith('failed to')) {
            return false;
         }
         return true;
      }
   }
};
</script>

<style scoped>
/* Transition arrow in log messages */
:deep(.transition-arrow) {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   margin: 0 6px;
   font-size: 12px;
   font-weight: 700;
   color: #00A0C1;
   vertical-align: middle;
}

/* Vertex UI Design System Components */
.vertex-header-banner {
   background: #ffffff;
   border: 1px solid #e2e8f0;
   border-left: 4px solid #00A0C1;
   border-radius: 10px;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.vertex-icon-circle {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: #e7f6f9;
   color: #00A0C1;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 20px;
}
.vertex-title {
   color: #0e1b2a;
   font-size: 16px;
   letter-spacing: -0.2px;
}
.vertex-card {
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   background: #ffffff;
}
.vertex-badge-subtle {
   background: #e7f6f9;
   color: #00809a;
   font-weight: 600;
   font-size: 11px;
   padding: 4px 8px;
   border-radius: 6px;
}
.vertex-badge-blue {
   background: #00A0C1;
   color: #ffffff;
   font-weight: 600;
   font-size: 11px;
   padding: 4px 8px;
   border-radius: 6px;
}
.vertex-calc-pill {
   background: #f1f5f9;
   color: #334155;
   font-weight: 600;
   font-size: 11px;
   padding: 4px 8px;
   border-radius: 6px;
}

/* Vertex Button System */
.btn-vertex-primary {
   background: #00A0C1;
   border: 1px solid #00A0C1;
   color: #ffffff;
   font-weight: 500;
   font-size: 13px;
   height: 38px;
   border-radius: 8px;
   padding: 0 18px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   transition: all 0.15s ease;
}
.btn-vertex-primary:hover:not(:disabled) {
   background: #008ba8;
   border-color: #008ba8;
   color: #ffffff;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.3);
}
.btn-vertex-primary:disabled {
   opacity: 0.65;
   cursor: not-allowed;
}

.btn-vertex-danger {
   background: #ffffff;
   border: 1px solid #dc3545;
   color: #dc3545;
   font-weight: 500;
   font-size: 13px;
   height: 38px;
   border-radius: 8px;
   padding: 0 16px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   transition: all 0.15s ease;
}
.btn-vertex-danger:hover:not(:disabled) {
   background: #fef2f2;
   border-color: #dc3545;
   color: #dc3545;
}
.btn-vertex-danger:disabled {
   opacity: 0.65;
   cursor: not-allowed;
}

.btn-vertex-outline {
   background: #ffffff;
   border: 1px solid #00A0C1;
   color: #00A0C1;
   font-weight: 500;
   font-size: 13px;
   height: 38px;
   border-radius: 8px;
   padding: 0 16px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   transition: all 0.15s ease;
}
.btn-vertex-outline:hover {
   background: #e7f6f9;
   border-color: #00A0C1;
   color: #00809a;
}

.btn-vertex-sm {
   height: 28px !important;
   font-size: 12px !important;
   padding: 0 10px !important;
   border-radius: 6px !important;
}

/* Mode and Preset Pills */
.mode-pill {
   border: 1px solid #cbd5e1;
   background: #f8fafc;
   color: #475569;
   padding: 7px 12px;
   border-radius: 8px;
   font-size: 12px;
   font-weight: 600;
   transition: all 0.15s ease;
}
.mode-pill-active {
   background: #00A0C1 !important;
   color: #ffffff !important;
   border-color: #00A0C1 !important;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.25);
}

.preset-chip {
   border: 1px solid #cbd5e1;
   background: #ffffff;
   color: #475569;
   padding: 2px 8px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.15s ease;
}
.preset-chip:hover {
   border-color: #00A0C1;
   color: #00A0C1;
   background: #f0fdfa;
}
.preset-chip-active {
   background: #00A0C1 !important;
   color: #ffffff !important;
   border-color: #00A0C1 !important;
}

/* Module Checkbox Cards */
.module-card {
   border: 1px solid #e2e8f0;
   background: #f8fafc;
   transition: all 0.15s ease;
}
.module-card:hover {
   border-color: #00A0C1;
   background: #f0fdfa;
}
.module-card-active {
   border-color: #00A0C1 !important;
   background: #e7f6f9 !important;
}

.vertex-token-chip {
   background: #ecfdf5;
   border: 1px solid #a7f3d0;
   color: #065f46;
   border-radius: 6px;
   font-size: 11px;
   padding: 5px 10px;
   font-family: monospace;
}
.vertex-token-error {
   background: #fef2f2;
   border: 1px solid #fecaca;
   color: #991b1b;
   border-radius: 6px;
   font-size: 11px;
   padding: 5px 10px;
   font-family: monospace;
}

.cursor-pointer {
   cursor: pointer;
}

/* Table and Expandable Row Styling matching AuditLogs.vue */
.date-text {
   font-size: 12px;
   color: #64748b;
   font-family: monospace;
}
.module-pill {
   display: inline-flex;
   align-items: center;
   padding: 3px 8px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
}
.log-type-pill {
   display: inline-flex;
   align-items: center;
   padding: 3px 8px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
}
.type-create {
   background: #e6f9ee;
   color: #00873c;
}
.type-update {
   background: #e7f6f9;
   color: #00809a;
}
.type-delete {
   background: #fde8ea;
   color: #c62828;
}
.type-auth {
   background: #fff8e1;
   color: #e6a100;
}
.log-message {
   color: #0e1b2a;
   font-size: 13px;
   font-weight: 400;
}
.performer-name {
   color: #0e1b2a;
   font-size: 13px;
   font-weight: 500;
   display: inline-flex;
   align-items: center;
}
.performer-system {
   color: #64748b;
   font-size: 12px;
   font-style: italic;
}
.clickable-row {
   cursor: pointer;
   transition: background-color 0.15s ease;
}
.clickable-row:hover {
   background-color: #f8fafc;
}
.btn-expand {
   background: transparent;
   border: 1px solid #e2e8f0;
   color: #64748b;
   border-radius: 6px;
   width: 28px;
   height: 28px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 0;
   font-size: 11px;
   transition: all 0.15s ease;
}
.btn-expand-active {
   background: #00A0C1;
   border-color: #00A0C1;
   color: #ffffff;
}
.details-row {
   background-color: #fafbfd;
}
.details-container {
   display: flex;
   gap: 16px;
   padding: 12px 16px;
   background: #f8fafc;
   border-radius: 8px;
   border: 1px solid #e2e8f0;
   margin: 4px 8px 12px 8px;
}
.data-section {
   flex: 1;
   min-width: 0;
}
.data-label {
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   margin-bottom: 6px;
   display: flex;
   align-items: center;
}
.data-section pre {
   background: #ffffff;
   border: 1px solid #e2e8f0;
   border-radius: 6px;
   padding: 10px;
   margin: 0;
   font-family: monospace;
   font-size: 11px;
   line-height: 1.5;
   max-height: 220px;
   overflow-y: auto;
   color: #1e293b;
}

/* Syntax Highlighting Colors matching AuditLogs.vue */
:deep(.json-key) {
   color: #00809a;
   font-weight: 600;
}
:deep(.json-string) {
   color: #00873c;
}
:deep(.json-number) {
   color: #d97706;
}
:deep(.json-boolean) {
   color: #7c3aed;
   font-weight: 600;
}
:deep(.json-null) {
   color: #94a3b8;
   font-style: italic;
}

/* General Styles */
.tenant-setup-card {
   background: #ffffff;
   border-radius: 10px;
   transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.tenant-setup-card:hover {
   box-shadow: 0 4px 12px rgba(0,0,0,0.06) !important;
}
.custom-tabs-container {
   border: 1px solid #cbd5e1;
   background-color: #f8fafc;
}
.custom-tabs .nav-link {
   color: #475569;
   font-size: 13px;
   font-weight: 600;
   background: transparent;
   border: none;
   transition: all 0.2s ease;
}
.custom-tabs .nav-link:hover {
   color: #00A0C1;
}
.custom-tabs .nav-link.active {
   background-color: #00A0C1 !important;
   color: #fff !important;
   box-shadow: 0 2px 8px rgba(0, 160, 193, 0.25);
}

.diagnostic-card {
   border: none;
   border-radius: 12px;
   transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.diagnostic-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;
}
.filter-label {
   font-size: 11px;
   font-weight: 600;
   color: #64748b;
   text-transform: uppercase;
   letter-spacing: 0.3px;
   margin-bottom: 5px;
   display: block;
}
.select-custom, .input-custom {
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   font-size: 13px;
   height: 36px;
}
.select-custom:focus, .input-custom:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.15);
}
.job-id-badge {
   font-family: monospace;
   font-size: 11px;
   font-weight: 600;
   background: #f1f5f9;
   color: #475569;
   padding: 2px 6px;
   border-radius: 4px;
   white-space: nowrap;
}
.bg-success-soft {
   background-color: #f0fdf4 !important;
}
.bg-primary-soft {
   background-color: #eff6ff !important;
}
.bg-danger-soft {
   background-color: #fef2f2 !important;
}
.bg-secondary-soft {
   background-color: #f8fafc !important;
}
.xs-text {
   font-size: 12px;
}
.mainTable th {
   background-color: #f8fafc;
   color: #475569;
   font-size: 12px;
   font-weight: 600;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   border-bottom: 2px solid #e2e8f0;
}
.mainTable td {
   border-bottom: 1px solid #f1f5f9;
   padding: 12px 8px;
}

.text-light-gray {
   color: #cbd5e1;
}
.bg-success {
   background-color: #10b981 !important;
}
.bg-warning {
   background-color: #f59e0b !important;
}
.bg-danger {
   background-color: #ef4444 !important;
}
.terminal-body::-webkit-scrollbar {
   width: 8px;
}
.terminal-body::-webkit-scrollbar-track {
   background: #0f172a;
}
.terminal-body::-webkit-scrollbar-thumb {
   background: #1e293b;
   border-radius: 4px;
}
.terminal-body::-webkit-scrollbar-thumb:hover {
   background: #334155;
}
.btn-outline-danger {
   color: #ef4444;
   border-color: rgba(239, 68, 68, 0.4);
   background: transparent;
}
.btn-outline-danger:hover {
   background-color: #ef4444;
   color: #fff;
}
.btn-outline-success {
   color: #10b981;
   border-color: rgba(16, 185, 129, 0.4);
   background: transparent;
}
.btn-outline-success:hover {
   background-color: #10b981;
   color: #fff;
}
.log-row {
   border-bottom: 1px solid #1e293b;
   padding-bottom: 4px;
}

/* Browser Viewport Sandbox Styles */
.virtual-browser {
   border-radius: 12px;
   overflow: hidden;
   border: 1px solid #cbd5e1;
   box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
}
.browser-header {
   background-color: #f1f5f9;
   border-bottom: 1px solid #cbd5e1;
   padding: 10px 16px;
   display: flex;
   align-items: center;
   gap: 12px;
}
.browser-dots {
   display: flex;
   gap: 6px;
}
.browser-dot {
   width: 10px;
   height: 10px;
   border-radius: 50%;
}
.dot-red { background-color: #ef4444; }
.dot-yellow { background-color: #f59e0b; }
.dot-green { background-color: #10b981; }

.browser-bar {
   flex-grow: 1;
   display: flex;
   align-items: center;
}
.browser-address {
   background-color: #ffffff;
   border: 1px solid #cbd5e1;
   border-radius: 8px;
   padding: 4px 12px;
   font-size: 13px;
   color: #334155;
   width: 100%;
   display: flex;
   align-items: center;
   gap: 8px;
}
.browser-viewport {
   background-color: #f8fafc;
   min-height: 380px;
   display: flex;
   flex-direction: column;
}
.text-pink {
   color: #f472b6 !important;
}
.bg-warning-soft {
   background-color: #fffbef !important;
}
.vertex-badge-green {
   background-color: #ECFDF5 !important;
   color: #059669 !important;
   border: 1px solid rgba(16, 185, 129, 0.25) !important;
}
.isolation-icon-badge {
   width: 38px;
   height: 38px;
   border-radius: 8px;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 18px;
}
.bg-success-subtle {
   background-color: #ECFDF5 !important;
}
.bg-danger-subtle {
   background-color: #FEF2F2 !important;
}

/* Status pills matching AuditLogs.vue */
.status-pill {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   gap: 3px;
   white-space: nowrap;
}
.status-success { background: #e6f9ee; color: #00873c; }
.status-failed  { background: #fde8ea; color: #c62828; }

/* Simulated badge */
.simulated-pill {
   background: #f1f5f9;
   color: #475569;
   border: 1px solid #cbd5e1;
   padding: 2px 8px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   line-height: 1.4;
   white-space: nowrap;
}
.simulated-pill i {
   color: #64748b;
   font-size: 10px;
}

/* Custom Modal Backdrop & Content matching Vertex UI */
.modal-backdrop-custom {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background-color: rgba(15, 23, 42, 0.55);
   backdrop-filter: blur(4px);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
   animation: fadeIn 0.15s ease-out;
}
.vertex-modal-card {
   position: relative;
   background: #ffffff;
   padding: 1.75rem;
   border-radius: 14px;
   width: 92%;
   max-width: 500px;
   box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.2);
   border: 1px solid #e2e8f0;
   animation: slideUp 0.2s ease-out;
}
.close-button-custom {
   position: absolute;
   top: 1rem;
   right: 1.25rem;
   font-size: 1.6rem;
   font-weight: 400;
   line-height: 1;
   color: #94a3b8;
   background: none;
   border: none;
   cursor: pointer;
   transition: color 0.15s ease;
}
.close-button-custom:hover {
   color: #0f172a;
}
.modal-title-custom {
   font-weight: 700;
   font-size: 1.15rem;
   letter-spacing: -0.2px;
}
.vertex-modal-card hr {
   margin: 0.85rem 0 1.15rem;
   border-color: #f1f5f9;
}
.vertex-icon-circle-danger {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: #fee2e2;
   color: #dc2626;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 18px;
   flex-shrink: 0;
}
.alert-danger-vertex {
   background-color: #fef2f2;
   border: 1px solid #fecaca;
   border-left: 4px solid #dc2626;
   border-radius: 8px;
   padding: 10px 14px;
   color: #991b1b;
}
.vertex-modal-details-card {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 8px;
   padding: 4px 14px;
}
.modal-footer-custom {
   margin-top: 1.25rem;
   display: flex;
   justify-content: flex-end;
   gap: 10px;
}
.btn-vertex-danger-solid {
   background: #dc2626;
   border: 1px solid #dc2626;
   color: #ffffff;
   font-weight: 500;
   font-size: 13px;
   height: 38px;
   border-radius: 8px;
   padding: 0 18px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   transition: all 0.15s ease;
}
.btn-vertex-danger-solid:hover:not(:disabled) {
   background: #b91c1c;
   border-color: #b91c1c;
   box-shadow: 0 2px 8px rgba(220, 38, 38, 0.35);
}
.btn-vertex-danger-solid:disabled {
   opacity: 0.65;
   cursor: not-allowed;
}

@keyframes fadeIn {
   from { opacity: 0; }
   to { opacity: 1; }
}
@keyframes slideUp {
   from { opacity: 0; transform: translateY(12px) scale(0.98); }
   to { opacity: 1; transform: translateY(0) scale(1); }
}
</style>
